import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const Priser = () => {
  // State for specialized treatment accordions
  const [activeSpecialty, setActiveSpecialty] = useState(null);

  const toggleSpecialty = (index) => {
    if (activeSpecialty === index) {
      setActiveSpecialty(null);
    } else {
      setActiveSpecialty(index);
    }
  };

  // State for economic support accordions
  const [activeSupport, setActiveSupport] = useState(null);

  const toggleSupport = (index) => {
    if (activeSupport === index) {
      setActiveSupport(null);
    } else {
      setActiveSupport(index);
    }
  };

  // State for FAQ accordions
  const [activeFaq, setActiveFaq] = useState(null);

  const toggleFaq = (index) => {
    if (activeFaq === index) {
      setActiveFaq(null);
    } else {
      setActiveFaq(index);
    }
  };

  const faqItems = [
    {
      question: "Binder jeg mig til et længere forløb, når jeg booker første behandlingssession?",
      answer: "Nej. Første behandlingssession er en selvstændig session på 50 minutter til 375 kr., hvor vi afklarer din situation, laver det første terapeutiske arbejde og udarbejder en behandlingsplan. Du forpligter dig ikke til noget videre forløb, men tager stilling i dit eget tempo bagefter."
    },
    {
      question: "Kan jeg starte med én session og først senere vælge et klippekort?",
      answer: "Ja, absolut. Mange klienter vælger at starte med første behandlingssession eller en individuel session for at mærke kemien og metodens virkning. Hvis du herefter ønsker et mere sammenhængende forløb, kan du overgå til et klippekort med 5 eller 10 sessioner."
    },
    {
      question: "Hvornår giver et klippekort bedst mening?",
      answer: "Et klippekort giver bedst mening, når du ønsker kontinuitet, tættere opfølgning og allerede ved, at du vil arbejde med din problematik over flere sessioner. Det er især fordelagtigt ved tilbagevendende mønstre, længerevarende stress, angst, tankemylder, søvnbesvær eller personlig udvikling, hvor løbende justering og materiale mellem sessionerne styrker processen."
    },
    {
      question: "Kan jeg skifte mellem online sessioner og sessioner i Valby?",
      answer: "Ja. Du kan frit veksle mellem fysiske sessioner i klinikken på Valby Tingsted 7 og rolige onlinesessioner via video, alt efter hvad der passer bedst ind i din hverdag."
    },
    {
      question: "Kan sessionerne indeholde både metakognitiv terapi og hypnose?",
      answer: "Ja. Sessionerne tilpasses altid fleksibelt efter dit behov. Behandlingen kan kombinere metakognitiv terapi og hypnose/hypnoterapi, eller have rent fokus på den ene tilgang, hvis det vurderes mest hensigtsmæssigt for din situation."
    },
    {
      question: "Kan rabatter kombineres med klippekort?",
      answer: "Klippekortene har i forvejen en indbygget mængderabat (1.700 kr. pr. session ved 5 sessioner og 1.500 kr. pr. session ved 10 sessioner). Særlige rabatter som pensionist-, studierabat (1.350 kr.) eller Core-rabat ved lægehenvisning (1.500 kr.) gælder derfor på almindelige individuelle enkeltsessioner."
    },
    {
      question: "Hvad sker der, hvis jeg ikke får brugt alle sessionerne inden gyldighedsperioden udløber?",
      answer: "Et klippekort med 5 sessioner er gyldigt i 4 måneder fra købsdato, mens et 10-sessioners forløb er gyldigt i 8 måneder. Tidsrammen er sat for at sikre fremdrift og kontinuitet i dit terapeutiske forløb. Forventer du længere pauser, anbefales det at booke enkeltsessioner fra gang til gang."
    },
    {
      question: "Kan jeg få en faktura, hvis sessionerne er erhvervsrettede?",
      answer: "Ja. Ved erhvervscoaching kan der udstedes en faktura direkte til din virksomhed eller arbejdsgiver. Du skal selv afklare med revisor eller bogholder, om udgiften kan fratrækkes."
    }
  ];

  const specialties = [
    {
      title: "Angstforløb",
      price: "Fra 8.500 kr.",
      icon: "fa-light fa-brain-circuit",
      description: "Angstforløbet er til dig, der oplever bekymringer, panik, kropsuro, katastrofetanker, social angst, præstationsangst eller en vedvarende følelse af alarmberedskab.",
      details: "I forløbet arbejder vi med de processer, der ofte holder angsten i gang: bekymring, grublerier, kropsscanning, undgåelse, sikkerhedsadfærd og forsøg på at kontrollere tanker og symptomer. Fokus er ikke at fjerne alle tanker eller følelser, men at ændre dit forhold til dem, så angsten får mindre plads.\n\nForløbet kan indeholde metakognitive øvelser, opmærksomhedstræning, arbejde med triggere, reduktion af bekymringstid, afkoblet opmærksomhed og konkrete strategier til hverdagssituationer."
    },
    {
      title: "Eksamensangst & præstationsangst",
      price: "Fra 8.500 kr.",
      icon: "fa-light fa-graduation-cap",
      description: "Forløbet mod eksamensangst og præstationsangst er til dig, der oplever hæmmende nervøsitet, frygt for at gå blank, katastrofetanker eller fysisk ubehag før og under præstationer.",
      details: "Vi arbejder målrettet med at bryde det mønster, der holder præstationsangsten ved lige: bekymringer i ugerne op til, overforberedelse, overvågning af egne symptomer og frygten for andres vurdering.\n\nFokus er at genvinde den mentale kontrol, så du kan have din opmærksomhed på opgaven frem for på din nervøsitet. Forløbet tilpasses studerende og professionelle og kan indeholde metakognitive øvelser, opmærksomhedstræning og hypnotisk ro-træning.\n\n* Studerende får 25 % rabat på almindelige individuelle sessioner (1.350 kr. pr. session)."
    },
    {
      title: "Helbredsangst",
      price: "Fra 8.500 kr.",
      icon: "fa-light fa-shield-check",
      description: "Forløbet mod helbredsangst (sygdomsangst) er til dig, der er plaget af vedvarende bekymringer om alvorlig sygdom, konstant kropsscanning og hyppige tjek af symptomer.",
      details: "Vi arbejder med at reducere den overdrevne opmærksomhed på kroppens naturlige signaler og bryde trangen til konstant at søge beroligelse (fx ved googling af symptomer eller gentagne lægebesøg).\n\nGennem metakognitiv terapi lærer du at lade bekymringerne om sygdom ligge, så de ikke længere styrer din hverdag. Målet er at genfinde en grundlæggende tryghed i din krop og slippe det konstante alarmberedskab."
    },
    {
      title: "Depression-forløb",
      price: "Fra 8.500 kr.",
      icon: "fa-light fa-cloud-sun",
      description: "Depression-forløbet er til dig, der oplever tristhed, lav energi, håbløshed, manglende motivation, selvkritik, tunghed, skyldfølelse eller en oplevelse af at være gået i stå.",
      details: "I metakognitiv terapi ser vi især på de tankeprocesser, der kan fastholde depression: grublerier, indre analyser, selvkritik, forsøg på at finde svar i fortiden og gentagne spørgsmål som “hvorfor har jeg det sådan?” eller “hvad er der galt med mig?”.\n\nForløbet hjælper dig med at reducere den tid og energi, der bruges på indre kamp, selvkritik og mental problemløsning. Der arbejdes med opmærksomhed, handlekraft, nye strategier og en mere fleksibel måde at forholde sig til tanker og følelser på.\n\nVed svær depression, selvmordstanker eller alvorlig mistrivsel anbefales det altid at kontakte egen læge eller akut hjælp."
    },
    {
      title: "Stressforløb",
      price: "Fra 8.500 kr.",
      icon: "fa-light fa-wind",
      description: "Stressforløbet er til dig, der føler dig overbelastet, presset, mentalt udmattet eller fanget i konstant problemløsning, bekymring og indre pres.",
      details: "Vi arbejder med de mønstre, der ofte vedligeholder stress: konstant tjekken indad, overansvar, grublerier, bekymringer, dårlig samvittighed, perfektionisme og følelsen af aldrig at kunne koble fra.\n\nForløbet kan indeholde metakognitive teknikker, opmærksomhedstræning, struktur i hverdagen, reduktion af mental overaktivitet og støtte til at genfinde overskud, retning og beslutningskraft."
    },
    {
      title: "OCD-forløb",
      price: "Fra 8.500 kr.",
      icon: "fa-light fa-arrows-spin",
      description: "OCD-forløbet er til dig, der oplever tvangstanker, tvivl, kontrolbehov, mental tjekning, gentagne ritualer, forsikringer eller stærk trang til at neutralisere ubehagelige tanker.",
      details: "I forløbet arbejder vi med forholdet til tvangstankerne frem for indholdet af dem. Målet er ikke at bevise, at tankerne er rigtige eller forkerte, men at ændre den måde du reagerer på dem.\n\nDer arbejdes med metakognitive antagelser om tanker, trangen til sikkerhed, mental kontrol, grublerier, tjekkeadfærd og strategier, der kortvarigt dæmper uroen, men langsigtet fastholder OCD-mønstret.\n\nForløbet kan indeholde afkoblet opmærksomhed, responsforebyggelse, arbejde med usikkerhed, reduktion af ritualer og konkrete øvelser til hverdagen."
    },
    {
      title: "PTSD-forløb / traumerelateret belastning",
      price: "Fra 8.500 kr.",
      icon: "fa-light fa-shield-heart",
      description: "PTSD-forløbet er til dig, der oplever efterreaktioner efter voldsomme, belastende eller traumatiske oplevelser. Det kan være flashbacks, mareridt, uro i kroppen, undgåelse, vagtsomhed, skyld, skam eller en følelse af, at nervesystemet stadig er i alarm.",
      details: "I forløbet arbejder vi nænsomt og struktureret med de processer, der kan holde traumereaktioner ved lige: bekymring, grublerier, overvågning af kroppen, undgåelse, forsøg på at kontrollere minder og stærke følelsesmæssige reaktioner.\n\nFokus er at skabe mere stabilitet, tryghed og fleksibilitet i hverdagen. Der kan arbejdes med opmærksomhed, regulering, metakognitive strategier, hypnotisk ro-træning og støtte til at møde triggere på en mere hensigtsmæssig måde.\n\nForløbet tilpasses altid nænsomt. Ved alvorlig PTSD, dissociation, selvskade eller akut krise anbefales kontakt til egen læge, psykiatrien eller relevant akut hjælp."
    },
    {
      title: "ADHD-forløb",
      price: "Fra 8.500 kr.",
      icon: "fa-light fa-bolt",
      description: "ADHD-forløbet er til dig, der oplever uro, overstimulering, tankemylder, impulsivitet, udsættelse, manglende overblik, følelsesmæssige udsving eller en oplevelse af hele tiden at være bagud.",
      details: "Forløbet er ikke en ADHD-udredning, men et terapeutisk og praktisk støtteforløb til dig, der har ADHD, ADHD-lignende vanskeligheder eller oplever belastninger, som ofte følger med.\n\nVi arbejder med opmærksomhed, struktur, selvkritik, stressbelastning, skam, indre uro og de tankeprocesser, der kan gøre hverdagen tungere. Der kan også arbejdes med beslutningskraft, vaner, energiforvaltning og mere realistiske rammer.\n\nForløbet kan indeholde metakognitive øvelser, visuelle planer, korte handlingsstrategier, strukturkort, prioriteringsværktøjer, opmærksomhedstræning og konkrete redskaber til at skabe mere ro og overblik i hverdagen."
    },
    {
      title: "Autismevenligt forløb",
      price: "Fra 8.500 kr.",
      icon: "fa-light fa-puzzle-piece",
      description: "Det autismevenlige forløb er til dig, der har autisme, autistiske træk eller oplever belastninger som overstimulering, social udmattelse, sansefølsomhed, angst, rigiditet, tankemylder eller autistisk burnout.",
      details: "Forløbet handler ikke om at fjerne autisme, men om at skabe bedre trivsel, mindre belastning og mere overskuelige rammer. Terapien tilpasses med tydelig struktur, forudsigelighed, konkret sprog og god tid til at forstå og omsætte redskaberne.\n\nDer kan anvendes visuelle støtter og konkrete redskaber som strukturkort, visuelle dagsplaner, følelsestermometer, belastningsskalaer, energiregnskab, social stories, tegneseriesamtaler, konkrete valgmuligheder og eventuelt PECS-inspireret billedstøtte, hvis det passer til klientens behov.\n\nFokus kan være angst, overbelastning, hverdagsstruktur, energi, kommunikation, sansefølsomhed, selvforståelse og mere bæredygtige strategier i hverdagen."
    },
    {
      title: "Søvnforløb",
      price: "3 sessioner / 4.950 kr.",
      icon: "fa-light fa-bed",
      description: "Søvnforløbet er til dig, der kæmper med søvnproblemer, tankemylder om aftenen, uro i kroppen eller frygt for ikke at kunne sove.",
      details: "Vi arbejder med kampen mod søvnen, bekymringer om søvn, overvågning af kroppen og de strategier, der ofte kommer til at fastholde søvnproblemet. Fokus er at skabe et mere afslappet forhold til søvn, tanker og kroppens signaler."
    },
    {
      title: "Selvværdsforløb",
      price: "Fra 8.500 kr.",
      icon: "fa-light fa-heart",
      description: "Selvværdsforløbet er til dig, der kæmper med selvkritik, usikkerhed, sammenligning, perfektionisme eller følelsen af ikke at være god nok.",
      details: "Vi arbejder med de tankeprocesser, der forstærker lavt selvværd: indre kritik, analyse af egne fejl, sammenligning med andre, bekymring om andres vurdering og forsøg på at finde sikkerhed.\n\nForløbet hjælper dig med at få mere afstand til selvkritiske tanker og opbygge en mere fri og fleksibel måde at være dig selv på."
    },
    {
      title: "Rygestop-forløb",
      price: "3 sessioner / 5.400 kr.",
      icon: "fa-light fa-ban-smoking",
      description: "Rygestop-forløbet kombinerer samtale, hypnose og konkrete strategier til dig, der ønsker at stoppe med at ryge.",
      details: "Der arbejdes med vaner, triggere, cravings, identitet, motivation og strategier til at håndtere de situationer, hvor trangen typisk opstår."
    },
    {
      title: "Parterapi & relationscoaching",
      price: "Fra 9.250 kr.",
      icon: "fa-light fa-people-arrows",
      description: "Parterapi og relationscoaching er til jer eller dig, der ønsker hjælp til kommunikation, konflikter, afstand, usikkerhed eller gentagne mønstre i parforholdet.",
      details: "I forløbet arbejder vi med at identificere de uhensigtsmæssige mønstre og reaktioner, der skaber afstand eller konflikter. Vi har fokus på at opbygge en mere tryg og åben kommunikation, genfinde den gensidige forståelse og styrke jeres relation.\n\nFørste relationssession koster kun 375 kr. pr. person (50 minutter) og gælder uanset om du kommer alene eller sammen med din partner. Herefter koster en session 1.950 kr., mens et forløb på 5 sessioner koster 9.250 kr. og 10 sessioner koster 17.500 kr.\n\nHvis forløbet efter første session fortsætter individuelt, gælder prisen for individuel terapi: 1.800 kr."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Priser hos Corehypnose.dk | Se pris - Metakognitiv terapi, Hypnose</title>
        <meta name="description" content="Se priser i Corehypnose.dk, her får du overblik over hvad et forløb / session koster. Kontakt mig gerne for tilbud på forløb tlf. 27 20 46 71" />
        <meta property="og:title" content="Priser hos Corehypnose.dk | Se pris - Metakognitiv terapi, Hypnose" />
        <meta property="og:description" content="Se priser i Corehypnose.dk, her får du overblik over hvad et forløb / session koster. Kontakt mig gerne for tilbud på forløb tlf. 27 20 46 71" />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/8ff806db-b665-42b0-97e5-34422b7bc8b8.png" />
        <meta name="twitter:image" content="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/8ff806db-b665-42b0-97e5-34422b7bc8b8.png" />
        <link rel="canonical" href="https://corehypnose.dk/priser" />
      </Helmet>

      {/* Main Container with Warm Beige Background */}
      <div className="bg-[#FAF7F2] min-h-screen text-[#3B352F] font-sans selection:bg-[#193f10]/10 selection:text-[#193f10]">
        
        {/* HERO SECTION */}
        <section className="pt-24 pb-16 md:pt-32 md:pb-24">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
              {/* Left Column: Heading, Intro & Actions */}
              <div className="lg:col-span-7 text-left">
                <span className="text-[#862e0a] font-bold text-xs uppercase tracking-widest block mb-3">
                  Sammenlign dine muligheder
                </span>
                <h1 className="text-4xl sm:text-5xl lg:text-5.5xl font-serif text-[#193f10] mb-6 leading-[1.15] tracking-tight">
                  Hvad får du med de<br className="hidden sm:inline" /> forskellige forløb?
                </h1>
                <p className="text-lg md:text-xl text-[#3B352F]/90 leading-relaxed mb-8 font-normal max-w-xl">
                  Du får samme faglige grundlag hele vejen. Forskellen ligger i, hvor meget kontinuitet, opfølgning, materiale og støtte mellem sessionerne der følger med.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center pt-2">
                  <Link 
                    to="/bestil-tid" 
                    className="px-8 py-4 bg-[#193f10] text-white font-bold rounded-lg hover:bg-[#193f10]/90 transition-all duration-200 shadow-md shadow-[#193f10]/15 flex items-center justify-center gap-2.5"
                  >
                    <i className="fa-light fa-calendar-check" /> Book tid
                  </Link>
                  <Link 
                    to="/kontakt" 
                    className="bg-white border border-[#E8DDCE] text-[#3B352F] px-8 py-4 rounded-lg font-semibold hover:bg-[#FAF7F2] hover:border-[#193f10]/30 transition-all flex items-center justify-center gap-2.5 shadow-xs"
                  >
                    <i className="fa-light fa-envelope" /> Kontakt mig
                  </Link>
                </div>
              </div>

              {/* Right Column: Highlights, Integration & Note */}
              <div className="lg:col-span-5 space-y-4">
                <div className="bg-white/80 backdrop-blur-xs border border-[#E8DDCE] rounded-2xl p-6 md:p-7 shadow-xs">
                  <div className="w-10 h-10 rounded-xl bg-[#193f10]/10 text-[#193f10] flex items-center justify-center mb-4">
                    <i className="fa-light fa-brain text-xl" aria-hidden="true" />
                  </div>
                  <h2 className="text-xl font-serif text-[#193f10] mb-3">
                    Faglighed & tilpasning
                  </h2>
                  <p className="text-sm md:text-base text-[#3B352F]/85 leading-relaxed">
                    <strong className="font-semibold text-[#193f10]">Metakognitiv terapi og hypnose</strong> kan indgå efter behov. Et længere forløb giver dig mere sammenhæng, mere støtte og bedre mulighed for at følge udviklingen over tid.
                  </p>
                </div>

                {/* Small Note: Vigtigt om betaling */}
                <div className="bg-[#FAF7F2] border border-[#E8DDCE] rounded-xl p-4.5 flex gap-3.5 items-start">
                  <i className="fa-light fa-circle-exclamation text-[#862e0a] text-base mt-0.5 shrink-0" aria-hidden="true" />
                  <p className="text-xs text-[#3B352F]/85 leading-relaxed">
                    <strong className="font-semibold text-[#193f10]">Vigtigt om betaling:</strong> Betaling sker altid inden sessionen starter (via MobilePay eller bankoverførsel). Dette gælder både enkeltsessioner, forløb, klippekort og online-sessioner.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>



        {/* KOM GODT I GANG (START MED FØRSTE BEHANDLINGSSESSION) */}
        <section className="pb-12 md:pb-16 pt-0 bg-[#FAF7F2]/40">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-[#862e0a] font-bold text-xs uppercase tracking-widest block mb-3">
                Fire måder at starte på
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-[#193f10] mb-4">
                Fra første afklaring til et længere forløb
              </h2>
              <p className="text-base text-[#3B352F]/80 leading-relaxed">
                Her kan du hurtigt se både pris, varighed og hvad der følger med.
              </p>
            </div>

            {/* Cards Grid - 4 Columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
              
              {/* Card 1: Første behandlingssession */}
              <div className="bg-gradient-to-br from-[#123F13] to-[#173F35] text-white rounded-3xl p-6 sm:p-7 flex flex-col justify-between shadow-lg relative transition-all duration-300 hover:shadow-xl">
                <div className="absolute inset-0 rounded-3xl overflow-hidden pointer-events-none">
                  <div className="absolute -right-16 -bottom-16 w-48 h-48 rounded-full bg-white/5 border border-white/10" />
                </div>
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#b59b5a] text-[#123F13] text-[10px] font-bold uppercase tracking-widest px-3.5 py-1 rounded-full shadow-sm z-20 whitespace-nowrap">
                  God start
                </div>
                <div className="relative z-10">
                  <h3 className="text-xl font-serif text-white font-bold mb-2 mt-1">Første behandlingssession</h3>
                  <p className="text-xs text-white/85 mb-4 leading-relaxed min-h-[3rem]">
                    Til dig, der vil have overblik og mærke, om min tilgang passer til dig.
                  </p>
                  
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="text-4xl font-bold text-white tracking-tight">375</span>
                    <span className="text-base text-[#b59b5a]/90 font-medium">kr.</span>
                  </div>
                  <p className="text-xs text-[#b59b5a]/90 font-medium mb-1">1 × 50 minutter</p>
                  <p className="text-xs text-white/70 uppercase tracking-wider mb-6">Første behandlingssession</p>
                  
                  <ul className="space-y-3 mb-8 border-t border-white/10 pt-5">
                    <li className="flex items-start gap-2.5 text-xs text-white/90">
                      <i className="fa-light fa-circle-check text-[#b59b5a] text-sm shrink-0 mt-0.5" /> <span>Grundig afklaring</span>
                    </li>
                    <li className="flex items-start gap-2.5 text-xs text-white/90">
                      <i className="fa-light fa-circle-check text-[#b59b5a] text-sm shrink-0 mt-0.5" /> <span>Første terapeutiske arbejde</span>
                    </li>
                    <li className="flex items-start gap-2.5 text-xs text-white/90">
                      <i className="fa-light fa-circle-check text-[#b59b5a] text-sm shrink-0 mt-0.5" /> <span>Skriftlig behandlingsplan</span>
                    </li>
                    <li className="flex items-start gap-2.5 text-xs text-white/90">
                      <i className="fa-light fa-circle-check text-[#b59b5a] text-sm shrink-0 mt-0.5" /> <span>Adgang til journalnotater</span>
                    </li>
                    <li className="flex items-start gap-2.5 text-xs text-white/90">
                      <i className="fa-light fa-circle-check text-[#b59b5a] text-sm shrink-0 mt-0.5" /> <span>Valby eller online</span>
                    </li>
                  </ul>
                </div>
                
                <Link 
                  to="/bestil-tid" 
                  className="w-full py-3.5 bg-[#b59b5a] text-[#123F13] font-bold rounded-md hover:bg-[#a3884b] text-center transition-all duration-200 text-xs shadow-md relative z-10"
                >
                  Book første session
                </Link>
              </div>

              {/* Card 2: Individuel session */}
              <div className="bg-white border border-[#E8DDCE] rounded-2xl p-6 sm:p-7 flex flex-col justify-between hover:border-[#193f10]/40 hover:shadow-lg transition-all duration-300 relative">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#3B352F]/10 text-[#3B352F] text-[10px] font-bold uppercase tracking-widest px-3.5 py-0.5 rounded-full whitespace-nowrap">
                  Fleksibel
                </div>
                <div>
                  <h3 className="text-xl font-serif text-[#193f10] font-bold mb-2 mt-1">Individuel session</h3>
                  <p className="text-xs text-[#3B352F]/70 mb-4 leading-relaxed min-h-[3rem]">
                    Til dig, der ønsker fuldt terapeutisk arbejde uden at købe et samlet forløb.
                  </p>
                  
                  <div className="mb-3">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold text-[#193f10] tracking-tight">1.800</span>
                      <span className="text-base text-[#3B352F]/60 font-medium">kr.</span>
                    </div>
                    <p className="text-xs text-[#3B352F]/60 font-medium mt-1">1 × 50 minutter</p>
                  </div>

                  <div className="mb-6 space-y-1.5">
                    <div className="text-[11px] font-medium text-[#862e0a] bg-[#862e0a]/5 border border-[#862e0a]/15 px-2.5 py-1 rounded-md">
                      Pensionist / studerende: 1.350 kr.
                    </div>
                    <div className="text-[11px] text-[#3B352F]/70 italic">
                      Betal fra gang til gang
                    </div>
                  </div>
                  
                  <ul className="space-y-3 mb-8 border-t border-[#E8DDCE]/50 pt-5">
                    <li className="flex items-start gap-2.5 text-xs text-[#3B352F]/80">
                      <i className="fa-light fa-circle-check text-[#193f10] text-sm shrink-0 mt-0.5" /> <span>Metakognitiv terapi og hypnose</span>
                    </li>
                    <li className="flex items-start gap-2.5 text-xs text-[#3B352F]/80">
                      <i className="fa-light fa-circle-check text-[#193f10] text-sm shrink-0 mt-0.5" /> <span>Adgang til journalnotater</span>
                    </li>
                    <li className="flex items-start gap-2.5 text-xs text-[#3B352F]/80">
                      <i className="fa-light fa-circle-check text-[#193f10] text-sm shrink-0 mt-0.5" /> <span>Øvelser og strategier efter behov</span>
                    </li>
                    <li className="flex items-start gap-2.5 text-xs text-[#3B352F]/80">
                      <i className="fa-light fa-circle-check text-[#193f10] text-sm shrink-0 mt-0.5" /> <span>Materiale og lydfiler efter relevans</span>
                    </li>
                    <li className="flex items-start gap-2.5 text-xs text-[#3B352F]/60">
                      <i className="fa-light fa-circle-info text-[#862e0a] text-sm shrink-0 mt-0.5" /> <span>Telefonisk opfølgning kan tilkøbes</span>
                    </li>
                  </ul>
                </div>
                
                <Link 
                  to="/bestil-tid" 
                  className="w-full py-3.5 bg-[#193f10] text-white font-bold rounded-md hover:bg-[#193f10]/90 text-center transition-all duration-200 text-xs shadow-sm"
                >
                  Book individuel session
                </Link>
              </div>

              {/* Card 3: 5 sessioner */}
              <div className="bg-white border border-[#E8DDCE] rounded-2xl p-6 sm:p-7 flex flex-col justify-between hover:border-[#193f10]/40 hover:shadow-lg transition-all duration-300 relative">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#193f10]/10 text-[#193f10] text-[10px] font-bold uppercase tracking-widest px-3.5 py-0.5 rounded-full whitespace-nowrap">
                  Mere kontinuitet
                </div>
                <div>
                  <h3 className="text-xl font-serif text-[#193f10] font-bold mb-2 mt-1">5 sessioner</h3>
                  <p className="text-xs text-[#3B352F]/70 mb-4 leading-relaxed min-h-[3rem]">
                    Til dig, der ønsker et samlet forløb med løbende opfølgning og støtte mellem sessionerne.
                  </p>
                  
                  <div className="mb-3">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold text-[#193f10] tracking-tight">8.500</span>
                      <span className="text-base text-[#3B352F]/60 font-medium">kr.</span>
                    </div>
                    <p className="text-xs text-[#3B352F]/60 font-medium mt-1">5 × 50 minutter · gyldigt i 4 måneder</p>
                  </div>

                  <div className="mb-6">
                    <div className="text-[11px] font-semibold text-[#193f10] bg-[#193f10]/5 border border-[#193f10]/15 px-2.5 py-1 rounded-md">
                      1.700 kr. pr. session
                    </div>
                  </div>
                  
                  <ul className="space-y-3 mb-8 border-t border-[#E8DDCE]/50 pt-5">
                    <li className="flex items-start gap-2.5 text-xs text-[#3B352F]/80">
                      <i className="fa-light fa-circle-check text-[#193f10] text-sm shrink-0 mt-0.5" /> <span>Alt fra individuel behandling</span>
                    </li>
                    <li className="flex items-start gap-2.5 text-xs text-[#3B352F]/80">
                      <i className="fa-light fa-circle-check text-[#193f10] text-sm shrink-0 mt-0.5" /> <span>Løbende justering af behandlingsplan</span>
                    </li>
                    <li className="flex items-start gap-2.5 text-xs text-[#3B352F]/80">
                      <i className="fa-light fa-circle-check text-[#193f10] text-sm shrink-0 mt-0.5" /> <span>Øvelser, teknikker og strategier</span>
                    </li>
                    <li className="flex items-start gap-2.5 text-xs text-[#3B352F]/80">
                      <i className="fa-light fa-circle-check text-[#193f10] text-sm shrink-0 mt-0.5" /> <span>Materiale og relevante lydfiler</span>
                    </li>
                    <li className="flex items-start gap-2.5 text-xs text-[#3B352F]/80">
                      <i className="fa-light fa-circle-check text-[#193f10] text-sm shrink-0 mt-0.5" /> <span>Kort telefonisk opfølgning mellem sessioner uden ekstra betaling</span>
                    </li>
                  </ul>
                </div>
                
                <Link 
                  to="/bestil-tid" 
                  className="w-full py-3.5 bg-[#193f10] text-white font-bold rounded-md hover:bg-[#193f10]/90 text-center transition-all duration-200 text-xs shadow-sm"
                >
                  Vælg 5 sessioner
                </Link>
              </div>

              {/* Card 4: 10 sessioner */}
              <div className="bg-white border-2 border-[#b29b56] ring-4 ring-[#b29b56]/20 rounded-2xl p-6 sm:p-7 flex flex-col justify-between hover:border-[#a08a47] hover:shadow-xl transition-all duration-300 relative shadow-md">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#b29b56] text-white text-[10px] font-bold uppercase tracking-widest px-3.5 py-0.5 rounded-full whitespace-nowrap shadow-sm">
                  Mest sammenhæng
                </div>
                <div>
                  <h3 className="text-xl font-serif text-[#193f10] font-bold mb-2 mt-1">10 sessioner</h3>
                  <p className="text-xs text-[#3B352F]/70 mb-4 leading-relaxed min-h-[3rem]">
                    Til dig, der ønsker god tid, høj kontinuitet og et længere behandlingsforløb.
                  </p>
                  
                  <div className="mb-3">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold text-[#193f10] tracking-tight">15.000</span>
                      <span className="text-base text-[#3B352F]/60 font-medium">kr.</span>
                    </div>
                    <p className="text-xs text-[#3B352F]/60 font-medium mt-1">10 × 50 minutter · gyldigt i 8 måneder</p>
                  </div>

                  <div className="mb-6">
                    <div className="text-[11px] font-semibold text-[#193f10] bg-[#193f10]/5 border border-[#193f10]/15 px-2.5 py-1 rounded-md">
                      1.500 kr. pr. session
                    </div>
                  </div>
                  
                  <ul className="space-y-3 mb-8 border-t border-[#E8DDCE]/50 pt-5">
                    <li className="flex items-start gap-2.5 text-xs text-[#3B352F]/80">
                      <i className="fa-light fa-circle-check text-[#193f10] text-sm shrink-0 mt-0.5" /> <span>Alt fra 5-sessioners forløbet</span>
                    </li>
                    <li className="flex items-start gap-2.5 text-xs text-[#3B352F]/80">
                      <i className="fa-light fa-circle-check text-[#193f10] text-sm shrink-0 mt-0.5" /> <span>Mere tid til træning og opfølgning</span>
                    </li>
                    <li className="flex items-start gap-2.5 text-xs text-[#3B352F]/80">
                      <i className="fa-light fa-circle-check text-[#193f10] text-sm shrink-0 mt-0.5" /> <span>Løbende justering over længere tid</span>
                    </li>
                    <li className="flex items-start gap-2.5 text-xs text-[#3B352F]/80">
                      <i className="fa-light fa-circle-check text-[#193f10] text-sm shrink-0 mt-0.5" /> <span>Materiale og lydfiler gennem forløbet</span>
                    </li>
                    <li className="flex items-start gap-2.5 text-xs text-[#3B352F]/80">
                      <i className="fa-light fa-circle-check text-[#193f10] text-sm shrink-0 mt-0.5" /> <span>Kort telefonisk opfølgning mellem sessioner uden ekstra betaling</span>
                    </li>
                  </ul>
                </div>
                
                <Link 
                  to="/bestil-tid" 
                  className="w-full py-3.5 bg-[#193f10] text-white font-bold rounded-md hover:bg-[#193f10]/90 text-center transition-all duration-200 text-xs shadow-sm"
                >
                  Vælg 10 sessioner
                </Link>
              </div>

            </div>
          </div>
        </section>

        {/* RABAT VED LÆGEHENVISNING & TRYG BETALING */}
        <section className="py-16 bg-[#FAF7F2] border-b border-[#E8DDCE]/50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 items-stretch">
              
              {/* Column 1: Rabat ved lægehenvisning */}
              <div className="bg-white border border-[#E8DDCE] rounded-2xl p-8 md:p-10 flex flex-col justify-between shadow-sm hover:shadow-md transition-all duration-300">
                <div>
                  <span className="text-[#862e0a] font-bold text-xs uppercase tracking-widest block mb-2">
                    Core-rabat
                  </span>
                  <h3 className="text-2xl md:text-3xl font-serif text-[#193f10] mb-4">
                    Rabat ved lægehenvisning
                  </h3>
                  <div className="space-y-4 text-sm md:text-base text-[#3B352F]/85 leading-relaxed mb-8">
                    <p>
                      Hvis du har en lægehenvisning, tilbyder jeg en <strong>Core-rabat på 300 kr. pr. session</strong>. Det betyder, at en individuel session kan reduceres fra <strong>1.800 kr. til 1.500 kr.</strong>.
                    </p>
                    <p>
                      Core-rabatten er en rabatordning hos Core Hypnose og er ikke det samme som offentligt tilskud. Kontakt mig gerne, hvis du er i tvivl om, hvad der gælder for dig.
                    </p>
                  </div>
                </div>
                <div>
                  <Link 
                    to="/kontakt" 
                    className="inline-flex items-center justify-center bg-[#a63a01] text-white px-6 py-3.5 rounded-md font-bold hover:bg-[#a63a01]/90 transition-all shadow-md hover:shadow-lg text-sm"
                  >
                    Kontakt mig om rabat
                  </Link>
                </div>
              </div>

              {/* Column 2: Tryg betaling */}
              <div className="bg-white border border-[#E8DDCE] rounded-2xl p-8 md:p-10 flex flex-col justify-between shadow-sm hover:shadow-md transition-all duration-300">
                <div>
                  <span className="text-[#862e0a] font-bold text-xs uppercase tracking-widest block mb-2">
                    Tryg betaling
                  </span>
                  <h3 className="text-2xl md:text-3xl font-serif text-[#193f10] mb-4">
                    Praktisk om betaling
                  </h3>
                  <div className="space-y-6 text-sm md:text-base text-[#3B352F]/85 leading-relaxed">
                    <p className="font-semibold text-[#193f10]">
                      Betaling sker altid inden sessionen starter. Når betalingen er registreret, gennemføres sessionen som aftalt.
                    </p>
                    
                    <div className="border-t border-[#E8DDCE]/60 pt-6 space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#193f10]/10 flex items-center justify-center text-[#193f10] shrink-0 mt-0.5">
                          <i className="fa-light fa-location-dot text-sm" />
                        </div>
                        <div>
                          <h4 className="font-bold text-[#193f10] text-sm md:text-base">I klinikken</h4>
                          <p className="text-xs md:text-sm text-[#3B352F]/70">
                            Core Hypnose, Valby Tingsted 7, 2500 Valby.
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#193f10]/10 flex items-center justify-center text-[#193f10] shrink-0 mt-0.5">
                          <i className="fa-light fa-video text-sm" />
                        </div>
                        <div>
                          <h4 className="font-bold text-[#193f10] text-sm md:text-base">Online</h4>
                          <p className="text-xs md:text-sm text-[#3B352F]/70">
                            Du modtager link inden sessionen.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>



        {/* SAMMENLIGN DINE MULIGHEDER (NY SAMMENLIGNINGSSEKTION) */}
        <section className="py-20 md:py-28 bg-white border-b border-[#E8DDCE]/60">
          <div className="max-w-6xl mx-auto px-6">
            
            {/* Section Header */}
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-[#862e0a] font-bold text-xs uppercase tracking-widest block mb-3">
                Sammenligning
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-[#193f10] mb-5">
                Se hvad der følger med
              </h2>
              <p className="text-base text-[#3B352F]/80 leading-relaxed">
                Hvis du vil sammenligne mulighederne mere præcist, kan du se forskellen her.
              </p>
            </div>

            {/* Comparison Table Container */}
            <div className="bg-white border border-[#E8DDCE] rounded-2xl shadow-md overflow-hidden mb-16">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[760px]">
                  <thead>
                    <tr className="border-b border-[#E8DDCE] bg-[#FAF7F2]/80">
                      <th className="py-6 px-6 text-sm font-serif font-bold text-[#193f10] w-[28%]">
                        Indhold & ydelser
                      </th>
                      <th className="py-6 px-4 text-center w-[18%] border-l border-[#E8DDCE]/60">
                        <span className="block text-xs font-bold text-[#862e0a] uppercase tracking-wider mb-1">Intro</span>
                        <span className="block text-xl font-serif font-bold text-[#193f10]">375 kr.</span>
                        <span className="block text-xs font-semibold text-[#193f10]">Første session</span>
                        <span className="block text-[11px] text-[#3B352F]/60 mt-0.5">Kun nye klienter</span>
                      </th>
                      <th className="py-6 px-4 text-center w-[18%] border-l border-[#E8DDCE]/60">
                        <span className="block text-xs font-bold text-[#3B352F]/50 uppercase tracking-wider mb-1">Enkelt</span>
                        <span className="block text-xl font-serif font-bold text-[#193f10]">1.800 kr.</span>
                        <span className="block text-xs font-semibold text-[#193f10]">Individuel</span>
                        <span className="block text-[11px] text-[#862e0a] mt-0.5">1.350 kr. pensionist/stud.</span>
                      </th>
                      <th className="py-6 px-4 text-center w-[18%] border-l border-[#E8DDCE]/60 bg-[#193f10]/5">
                        <span className="block text-xs font-bold text-[#193f10] uppercase tracking-wider mb-1">Kontinuitet</span>
                        <span className="block text-xl font-serif font-bold text-[#193f10]">8.500 kr.</span>
                        <span className="block text-xs font-semibold text-[#193f10]">5 sessioner</span>
                        <span className="block text-[11px] text-[#3B352F]/70 mt-0.5">1.700 kr. pr. session</span>
                      </th>
                      <th className="py-6 px-4 text-center w-[18%] border-l border-[#E8DDCE]/60 bg-[#193f10]/10">
                        <span className="block text-xs font-bold text-[#b59b5a] uppercase tracking-wider mb-1">Mest sammenhæng</span>
                        <span className="block text-xl font-serif font-bold text-[#193f10]">15.000 kr.</span>
                        <span className="block text-xs font-semibold text-[#193f10]">10 sessioner</span>
                        <span className="block text-[11px] text-[#193f10] font-semibold mt-0.5">1.500 kr. pr. session</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E8DDCE]/60 text-xs md:text-sm text-[#3B352F]">
                    
                    {/* CATEGORY: Session og behandling */}
                    <tr className="bg-[#FAF7F2] font-serif font-bold text-[#193f10]">
                      <td colSpan={5} className="py-3 px-6 text-xs uppercase tracking-wider text-[#862e0a]">
                        Session og behandling
                      </td>
                    </tr>
                    <tr className="hover:bg-[#FAF7F2]/40 transition-colors">
                      <td className="py-4 px-6 font-medium text-[#193f10]">50 minutters session</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40">
                        <span className="inline-flex items-center gap-1.5 text-xs text-[#193f10] font-semibold">
                          <i className="fa-solid fa-check text-[#193f10]" /> 1 session
                        </span>
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40">
                        <span className="inline-flex items-center gap-1.5 text-xs text-[#193f10] font-semibold">
                          <i className="fa-solid fa-check text-[#193f10]" /> 1 session
                        </span>
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/5 font-semibold text-[#193f10]">
                        5 sessioner
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/10 font-bold text-[#193f10]">
                        10 sessioner
                      </td>
                    </tr>
                    <tr className="hover:bg-[#FAF7F2]/40 transition-colors">
                      <td className="py-4 px-6 font-medium text-[#193f10]">Valby eller online</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 text-[#193f10]">
                        <i className="fa-solid fa-check" />
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 text-[#193f10]">
                        <i className="fa-solid fa-check" />
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/5 text-[#193f10]">
                        <i className="fa-solid fa-check" />
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/10 text-[#193f10]">
                        <i className="fa-solid fa-check" />
                      </td>
                    </tr>
                    <tr className="hover:bg-[#FAF7F2]/40 transition-colors">
                      <td className="py-4 px-6 font-medium text-[#193f10]">Metakognitiv terapi og hypnose efter behov</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 text-[#193f10]">
                        <i className="fa-solid fa-check" />
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 text-[#193f10]">
                        <i className="fa-solid fa-check" />
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/5 text-[#193f10]">
                        <i className="fa-solid fa-check" />
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/10 text-[#193f10]">
                        <i className="fa-solid fa-check" />
                      </td>
                    </tr>
                    <tr className="hover:bg-[#FAF7F2]/40 transition-colors">
                      <td className="py-4 px-6 font-medium text-[#193f10]">Løbende opfølgning på udvikling</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 text-[#3B352F]/40">–</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 text-xs text-[#3B352F]/70">Ved ny session</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/5 text-[#193f10] font-semibold">
                        <i className="fa-solid fa-check mr-1" /> Løbende
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/10 text-[#193f10] font-bold">
                        <i className="fa-solid fa-check mr-1" /> Løbende
                      </td>
                    </tr>

                    {/* CATEGORY: Plan og dokumentation */}
                    <tr className="bg-[#FAF7F2] font-serif font-bold text-[#193f10]">
                      <td colSpan={5} className="py-3 px-6 text-xs uppercase tracking-wider text-[#862e0a]">
                        Plan og dokumentation
                      </td>
                    </tr>
                    <tr className="hover:bg-[#FAF7F2]/40 transition-colors">
                      <td className="py-4 px-6 font-medium text-[#193f10]">Skriftlig behandlingsplan</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 text-xs text-[#193f10] font-semibold">
                        Efter 1. session
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 text-xs text-[#3B352F]/70">
                        Ved opstart
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/5 text-[#193f10] font-semibold">
                        <i className="fa-solid fa-check" />
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/10 text-[#193f10] font-bold">
                        <i className="fa-solid fa-check" />
                      </td>
                    </tr>
                    <tr className="hover:bg-[#FAF7F2]/40 transition-colors">
                      <td className="py-4 px-6 font-medium text-[#193f10]">Adgang til journalnotater</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 text-[#193f10]">
                        <i className="fa-solid fa-check" />
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 text-[#193f10]">
                        <i className="fa-solid fa-check" />
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/5 text-[#193f10]">
                        <i className="fa-solid fa-check" />
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/10 text-[#193f10]">
                        <i className="fa-solid fa-check" />
                      </td>
                    </tr>
                    <tr className="hover:bg-[#FAF7F2]/40 transition-colors">
                      <td className="py-4 px-6 font-medium text-[#193f10]">Løbende justering af behandlingsplan</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 text-[#3B352F]/40">–</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 text-xs text-[#3B352F]/70">Efter behov</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/5 text-[#193f10] font-semibold">
                        <i className="fa-solid fa-check" />
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/10 text-[#193f10] font-bold">
                        <i className="fa-solid fa-check" />
                      </td>
                    </tr>

                    {/* CATEGORY: Materiale mellem sessionerne */}
                    <tr className="bg-[#FAF7F2] font-serif font-bold text-[#193f10]">
                      <td colSpan={5} className="py-3 px-6 text-xs uppercase tracking-wider text-[#862e0a]">
                        Materiale mellem sessionerne
                      </td>
                    </tr>
                    <tr className="hover:bg-[#FAF7F2]/40 transition-colors">
                      <td className="py-4 px-6 font-medium text-[#193f10]">Øvelser og teknikker</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 text-xs text-[#3B352F]/70">Ved relevans</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 text-xs text-[#3B352F]/70">Ved relevans</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/5 text-[#193f10] font-semibold">
                        <i className="fa-solid fa-check" /> Inkluderet
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/10 text-[#193f10] font-bold">
                        <i className="fa-solid fa-check" /> Inkluderet
                      </td>
                    </tr>
                    <tr className="hover:bg-[#FAF7F2]/40 transition-colors">
                      <td className="py-4 px-6 font-medium text-[#193f10]">Konkrete strategier til hverdagen</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 text-xs text-[#3B352F]/70">Ved relevans</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 text-xs text-[#3B352F]/70">Ved relevans</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/5 text-[#193f10] font-semibold">
                        <i className="fa-solid fa-check" /> Inkluderet
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/10 text-[#193f10] font-bold">
                        <i className="fa-solid fa-check" /> Inkluderet
                      </td>
                    </tr>
                    <tr className="hover:bg-[#FAF7F2]/40 transition-colors">
                      <td className="py-4 px-6 font-medium text-[#193f10]">Fremsendt behandlingsmateriale</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 text-xs text-[#3B352F]/70">Begrænset</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 text-xs text-[#3B352F]/70">Efter behov</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/5 text-[#193f10] font-semibold">
                        <i className="fa-solid fa-check" /> Inkluderet
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/10 text-[#193f10] font-bold">
                        <i className="fa-solid fa-check" /> Inkluderet
                      </td>
                    </tr>
                    <tr className="hover:bg-[#FAF7F2]/40 transition-colors">
                      <td className="py-4 px-6 font-medium text-[#193f10]">Lydfiler efter relevans</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 text-xs text-[#3B352F]/70">Efter behov</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 text-xs text-[#3B352F]/70">Efter behov</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/5 text-[#193f10] font-semibold">
                        <i className="fa-solid fa-check" /> Inkluderet
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/10 text-[#193f10] font-bold">
                        <i className="fa-solid fa-check" /> Inkluderet
                      </td>
                    </tr>

                    {/* CATEGORY: Kontakt mellem sessionerne */}
                    <tr className="bg-[#FAF7F2] font-serif font-bold text-[#193f10]">
                      <td colSpan={5} className="py-3 px-6 text-xs uppercase tracking-wider text-[#862e0a]">
                        Kontakt mellem sessionerne
                      </td>
                    </tr>
                    <tr className="hover:bg-[#FAF7F2]/40 transition-colors">
                      <td className="py-4 px-6 font-medium text-[#193f10]">Kort telefonisk opfølgning mellem sessioner</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 text-xs text-[#862e0a]">Kan tilkøbes</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 text-xs text-[#862e0a]">Kan tilkøbes</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/5 text-[#193f10] font-semibold text-xs">
                        <i className="fa-solid fa-check mr-1" /> Inkluderet*
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/10 text-[#193f10] font-bold text-xs">
                        <i className="fa-solid fa-check mr-1" /> Inkluderet*
                      </td>
                    </tr>
                    <tr className="hover:bg-[#FAF7F2]/40 transition-colors">
                      <td className="py-4 px-6 font-medium text-[#193f10]">Mulighed for korte afklaringer undervejs</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 text-[#3B352F]/40">–</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 text-xs text-[#862e0a]">Ved tilkøb</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/5 text-[#193f10] font-semibold">
                        <i className="fa-solid fa-check" />
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/10 text-[#193f10] font-bold">
                        <i className="fa-solid fa-check" />
                      </td>
                    </tr>

                    {/* CATEGORY: Forløb og værdi */}
                    <tr className="bg-[#FAF7F2] font-serif font-bold text-[#193f10]">
                      <td colSpan={5} className="py-3 px-6 text-xs uppercase tracking-wider text-[#862e0a]">
                        Forløb og værdi
                      </td>
                    </tr>
                    <tr className="hover:bg-[#FAF7F2]/40 transition-colors">
                      <td className="py-4 px-6 font-medium text-[#193f10]">Pris pr. session</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 font-semibold text-[#193f10]">
                        Intropris (375 kr.)
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 font-semibold text-[#193f10]">
                        1.800 kr.
                        <span className="block text-[11px] font-normal text-[#862e0a]">1.350 kr. stud./pens.</span>
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/5 font-bold text-[#193f10]">
                        1.700 kr.
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/10 font-bold text-[#193f10]">
                        1.500 kr.
                      </td>
                    </tr>
                    <tr className="hover:bg-[#FAF7F2]/40 transition-colors">
                      <td className="py-4 px-6 font-medium text-[#193f10]">Gyldighed</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 text-[#3B352F]/40">–</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 text-[#3B352F]/40">–</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/5 font-semibold text-[#193f10]">
                        4 måneder
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/10 font-bold text-[#193f10]">
                        8 måneder
                      </td>
                    </tr>
                    <tr className="hover:bg-[#FAF7F2]/40 transition-colors">
                      <td className="py-4 px-6 font-medium text-[#193f10]">Sammenhæng i forløbet</td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 text-xs font-semibold text-[#862e0a]">
                        Opstart & afklaring
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 text-xs font-semibold text-[#3B352F]">
                        Fleksibel
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/5 text-xs font-bold text-[#193f10]">
                        Høj kontinuitet
                      </td>
                      <td className="py-4 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/10 text-xs font-bold text-[#193f10]">
                        Mest sammenhæng
                      </td>
                    </tr>
                    
                    {/* Action row */}
                    <tr className="bg-[#FAF7F2]/80">
                      <td className="py-5 px-6 font-semibold text-xs text-[#3B352F]/70">
                        Vælg løsning
                      </td>
                      <td className="py-5 px-4 text-center border-l border-[#E8DDCE]/40">
                        <Link 
                          to="/bestil-tid" 
                          className="inline-block px-3 py-2 bg-[#b59b5a] text-[#123F13] font-bold text-xs rounded hover:bg-[#a3884b] transition-all"
                        >
                          Book 375 kr.
                        </Link>
                      </td>
                      <td className="py-5 px-4 text-center border-l border-[#E8DDCE]/40">
                        <Link 
                          to="/bestil-tid" 
                          className="inline-block px-3 py-2 bg-[#193f10] text-white font-bold text-xs rounded hover:bg-[#193f10]/90 transition-all"
                        >
                          Book 1.800 kr.
                        </Link>
                      </td>
                      <td className="py-5 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/5">
                        <Link 
                          to="/bestil-tid" 
                          className="inline-block px-3 py-2 bg-[#193f10] text-white font-bold text-xs rounded hover:bg-[#193f10]/90 transition-all"
                        >
                          Vælg 5 sessioner
                        </Link>
                      </td>
                      <td className="py-5 px-4 text-center border-l border-[#E8DDCE]/40 bg-[#193f10]/10">
                        <Link 
                          to="/bestil-tid" 
                          className="inline-block px-3 py-2 bg-[#193f10] text-white font-bold text-xs rounded hover:bg-[#193f10]/90 transition-all"
                        >
                          Vælg 10 sessioner
                        </Link>
                      </td>
                    </tr>

                  </tbody>
                </table>
              </div>

              {/* Table Legend */}
              <div className="p-6 bg-[#FAF7F2] border-t border-[#E8DDCE] text-xs text-[#3B352F]/80 flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-5">
                  <span className="flex items-center gap-1.5 font-semibold text-[#193f10]">
                    <i className="fa-solid fa-check text-xs" /> Inkluderet
                  </span>
                  <span className="flex items-center gap-1.5 text-[#3B352F]/70">
                    <span className="font-semibold text-[#193f10]">Efter behov:</span> Afhænger af behandlingen
                  </span>
                  <span className="flex items-center gap-1.5 text-[#3B352F]/70">
                    <span className="font-semibold">–</span> Ikke inkluderet som fast del
                  </span>
                </div>
                <p className="text-[11px] text-[#3B352F]/70 italic">
                  * Telefonisk opfølgning i et forløb er tænkt som korte relevante afklaringer mellem sessionerne, ikke som en ekstra behandlingssession.
                </p>
              </div>
            </div>

          </div>

          {/* HVAD BETYDER FORSKELLEN? - FULD BREDDE SEKTION */}
          <div className="w-full bg-[#FAF7F2] border-y border-[#E8DDCE] py-16 md:py-20 mb-20">
            <div className="max-w-6xl mx-auto px-6">
              <div className="text-center max-w-3xl mx-auto mb-12">
                <span className="text-[#862e0a] font-bold text-xs uppercase tracking-widest block mb-2">
                  Hvad betyder forskellen?
                </span>
                <h3 className="text-2xl md:text-3xl font-serif text-[#193f10] mb-3">
                  Jo længere forløb, jo mere støtte omkring behandlingen
                </h3>
                <p className="text-sm md:text-base text-[#3B352F]/80 leading-relaxed">
                  Du betaler ikke for en anden metode, men for mere sammenhæng, mere opfølgning og mere støtte undervejs.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                
                {/* Box 1 */}
                <div className="bg-white border border-[#E8DDCE] rounded-2xl p-6 flex flex-col justify-between hover:shadow-md transition-all">
                  <div>
                    <h4 className="text-xl font-serif font-bold text-[#193f10] mb-2">375 kr.</h4>
                    <p className="text-xs text-[#3B352F]/80 leading-relaxed">
                      <strong>Afklaring og retning.</strong> Du får en reel første behandlingssession, en behandlingsplan og et klart udgangspunkt.
                    </p>
                  </div>
                </div>

                {/* Box 2 */}
                <div className="bg-white border border-[#E8DDCE] rounded-2xl p-6 flex flex-col justify-between hover:shadow-md transition-all">
                  <div>
                    <h4 className="text-xl font-serif font-bold text-[#193f10] mb-2">1.800 kr.</h4>
                    <p className="text-xs text-[#3B352F]/80 leading-relaxed">
                      <strong>Fuld fleksibilitet.</strong> Du booker én individuel session ad gangen og bestemmer selv tempoet. Pensionister og studerende betaler 1.350 kr. pr. session.
                    </p>
                  </div>
                </div>

                {/* Box 3 */}
                <div className="bg-white border-2 border-[#193f10]/30 rounded-2xl p-6 flex flex-col justify-between hover:shadow-md transition-all bg-[#193f10]/[0.02]">
                  <div>
                    <h4 className="text-xl font-serif font-bold text-[#193f10] mb-2">5 sessioner</h4>
                    <p className="text-xs text-[#3B352F]/80 leading-relaxed">
                      <strong>Kontinuitet.</strong> Du får et samlet forløb, løbende materiale og telefonisk opfølgning uden ekstra betaling.
                    </p>
                  </div>
                </div>

                {/* Box 4 */}
                <div className="bg-white border-2 border-[#193f10] rounded-2xl p-6 flex flex-col justify-between shadow-md transition-all bg-[#193f10]/[0.03]">
                  <div>
                    <h4 className="text-xl font-serif font-bold text-[#193f10] mb-2">10 sessioner</h4>
                    <p className="text-xs text-[#3B352F]/80 leading-relaxed">
                      <strong>Mest sammenhæng.</strong> Du får mere tid til at træne, evaluere, justere og fastholde arbejdet over længere tid.
                    </p>
                  </div>
                </div>

              </div>
            </div>
          </div>

          <div className="max-w-6xl mx-auto px-6">

            {/* HVIS DU ER I TVIVL */}
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
                {/* Venstre kasse med tekst */}
                <div className="bg-white border border-[#E8DDCE] rounded-2xl p-6 md:p-8 flex flex-col justify-center h-full shadow-xs">
                  <span className="text-[#862e0a] font-bold text-xs uppercase tracking-widest block mb-2">
                    Hvis du er i tvivl
                  </span>
                  <h3 className="text-2xl md:text-3xl font-serif text-[#193f10] mb-4">
                    Et godt sted at begynde
                  </h3>
                  <p className="text-sm md:text-base text-[#3B352F]/85 leading-relaxed mb-4">
                    Mange starter med første behandlingssession. Her får du både afklaring, første terapeutiske arbejde og en behandlingsplan, så du ikke behøver beslutte hele forløbet på forhånd.
                  </p>
                  <p className="text-sm text-[#3B352F]/70 leading-relaxed">
                    Hvis du allerede ved, at du ønsker mere kontinuitet, kan et forløb med 5 eller 10 sessioner være en god løsning.
                  </p>
                </div>

                {/* Højre side: 4 mindre kasser med de andre punkter */}
                <div className="flex flex-col gap-3.5 justify-between">
                  {/* 1 */}
                  <div className="bg-white border border-[#E8DDCE] rounded-2xl p-5 flex items-start gap-4 shadow-xs flex-1">
                    <div className="w-9 h-9 rounded-full bg-[#b59b5a] text-[#123F13] font-bold text-sm flex items-center justify-center shrink-0 mt-0.5">
                      1
                    </div>
                    <div>
                      <h4 className="font-bold text-[#193f10] text-sm mb-1">
                        375 kr. → indsigt og retning
                      </h4>
                      <p className="text-xs text-[#3B352F]/75 leading-relaxed">
                        Afklaring, første arbejde og plan for næste skridt.
                      </p>
                    </div>
                  </div>

                  {/* 2 */}
                  <div className="bg-white border border-[#E8DDCE] rounded-2xl p-5 flex items-start gap-4 shadow-xs flex-1">
                    <div className="w-9 h-9 rounded-full bg-[#193f10] text-white font-bold text-sm flex items-center justify-center shrink-0 mt-0.5">
                      2
                    </div>
                    <div>
                      <h4 className="font-bold text-[#193f10] text-sm mb-1">
                        1.800 kr. → fuld individuel session
                      </h4>
                      <p className="text-xs text-[#3B352F]/75 leading-relaxed">
                        Fleksibilitet og terapeutisk arbejde fra gang til gang.
                      </p>
                    </div>
                  </div>

                  {/* 5 */}
                  <div className="bg-white border border-[#E8DDCE] rounded-2xl p-5 flex items-start gap-4 shadow-xs flex-1">
                    <div className="w-9 h-9 rounded-full bg-[#193f10] text-white font-bold text-sm flex items-center justify-center shrink-0 mt-0.5">
                      5
                    </div>
                    <div>
                      <h4 className="font-bold text-[#193f10] text-sm mb-1">
                        5 sessioner → kontinuitet
                      </h4>
                      <p className="text-xs text-[#3B352F]/75 leading-relaxed">
                        Et samlet forløb med opfølgning, materiale og støtte undervejs.
                      </p>
                    </div>
                  </div>

                  {/* 10 */}
                  <div className="bg-white border border-[#E8DDCE] rounded-2xl p-5 flex items-start gap-4 shadow-xs flex-1">
                    <div className="w-9 h-9 rounded-full bg-[#193f10] text-white font-bold text-sm flex items-center justify-center shrink-0 mt-0.5">
                      10
                    </div>
                    <div>
                      <h4 className="font-bold text-[#193f10] text-sm mb-1">
                        10 sessioner → mest sammenhæng
                      </h4>
                      <p className="text-xs text-[#3B352F]/75 leading-relaxed">
                        God tid til et længere behandlingsforløb med høj kontinuitet.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* PARTERAPI & RELATIONSSESSIONER */}
        <section className="py-20 md:py-28 bg-[#FAF7F2] border-y border-[#E8DDCE]/50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-[#862e0a] font-bold text-xs uppercase tracking-widest block mb-3">
                Parterapi & Relationscoaching
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-[#193f10] mb-4">
                Priser for parterapi og relationscoaching
              </h2>
              <p className="text-base text-[#3B352F]/80 leading-relaxed">
                Uanset om I kommer sammen som par, eller du ønsker at arbejde med dine relationer alene, tilbydes der en klar og tryg ramme for jeres forløb.
              </p>
            </div>

            {/* Relations Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch max-w-5xl mx-auto">
              
              {/* Card 1: Første relationssession */}
              <div className="bg-white border-2 border-[#862e0a] rounded-2xl p-8 flex flex-col justify-between shadow-xl relative">
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#862e0a] text-white text-[10px] font-bold uppercase tracking-widest px-4 py-1 rounded-full shadow-sm">
                  Anbefalet start
                </div>
                <div>
                  <h3 className="text-xl font-serif text-[#193f10] font-bold mb-1">Første relationssession</h3>
                  <p className="text-xs text-[#3B352F]/50 uppercase tracking-wider mb-4">50 minutter</p>
                  
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-4xl font-bold text-[#193f10] tracking-tight">375</span>
                    <span className="text-base text-[#3B352F]/60 font-medium">kr. pr. person</span>
                  </div>
                  
                  <p className="text-sm text-[#3B352F]/70 mb-6 leading-relaxed">
                    Gælder første session, uanset om du kommer alene eller sammen med din partner. Her afklarer vi jeres situation og lægger en plan.
                  </p>
                  
                  <ul className="space-y-3 mb-8 border-t border-[#E8DDCE]/50 pt-6">
                    <li className="flex items-center gap-2.5 text-xs text-[#3B352F]/80">
                      <i className="fa-light fa-circle-check text-[#862e0a] text-sm shrink-0" /> Fælles eller individuel start
                    </li>
                    <li className="flex items-center gap-2.5 text-xs text-[#3B352F]/80">
                      <i className="fa-light fa-circle-check text-[#862e0a] text-sm shrink-0" /> Afklaring af mønstre
                    </li>
                    <li className="flex items-center gap-2.5 text-xs text-[#3B352F]/80">
                      <i className="fa-light fa-circle-check text-[#862e0a] text-sm shrink-0" /> Konkrete redskaber med hjem
                    </li>
                  </ul>
                </div>
                
                <Link 
                  to="/bestil-tid" 
                  className="w-full py-3.5 bg-[#862e0a] text-white font-bold rounded-lg hover:bg-[#862e0a]/90 text-center transition-all duration-200 text-sm"
                >
                  Book første session
                </Link>
              </div>

              {/* Card 2: Enkelt session */}
              <div className="bg-white border border-[#E8DDCE] rounded-2xl p-8 flex flex-col justify-between hover:border-[#193f10]/40 hover:shadow-lg transition-all duration-300">
                <div>
                  <h3 className="text-xl font-serif text-[#193f10] font-bold mb-1">Parterapi / relationscoaching</h3>
                  <p className="text-xs text-[#3B352F]/50 uppercase tracking-wider mb-4">50 minutter</p>
                  
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-4xl font-bold text-[#193f10] tracking-tight">1.950</span>
                    <span className="text-base text-[#3B352F]/60 font-medium">kr.</span>
                  </div>
                  
                  <p className="text-sm text-[#3B352F]/70 mb-6 leading-relaxed">
                    En almindelig session for par eller enkeltpersoner, der ønsker at arbejde dybere med relationelle udfordringer.
                  </p>
                  
                  <ul className="space-y-3 mb-8 border-t border-[#E8DDCE]/50 pt-6">
                    <li className="flex items-center gap-2.5 text-xs text-[#3B352F]/80">
                      <i className="fa-light fa-circle-check text-[#193f10] text-sm shrink-0" /> Professionel parterapi
                    </li>
                    <li className="flex items-center gap-2.5 text-xs text-[#3B352F]/80">
                      <i className="fa-light fa-circle-check text-[#193f10] text-sm shrink-0" /> Relationscoaching
                    </li>
                    <li className="flex items-center gap-2.5 text-xs text-[#3B352F]/80">
                      <i className="fa-light fa-circle-check text-[#193f10] text-sm shrink-0" /> Konflikthåndtering
                    </li>
                  </ul>
                </div>
                
                <Link 
                  to="/bestil-tid" 
                  className="w-full py-3.5 bg-[#FAF7F2] text-[#193f10] border border-[#E8DDCE] font-bold rounded-md hover:bg-[#E8DDCE]/50 text-center transition-all duration-200 text-sm"
                >
                  Book session
                </Link>
              </div>

              {/* Card 3: Forløb & Klippekort */}
              <div className="bg-white border border-[#E8DDCE] rounded-2xl p-8 flex flex-col justify-between hover:border-[#193f10]/40 hover:shadow-lg transition-all duration-300">
                <div>
                  <h3 className="text-xl font-serif text-[#193f10] font-bold mb-1">Forløb & Klippekort</h3>
                  <p className="text-xs text-[#3B352F]/50 uppercase tracking-wider mb-4">Kontinuitet & rabat</p>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-xl font-bold text-[#193f10]">5 sessioner:</span>
                      <span className="text-lg font-bold text-[#193f10] tracking-tight">9.250 kr.</span>
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-xl font-bold text-[#193f10]">10 sessioner:</span>
                      <span className="text-lg font-bold text-[#193f10] tracking-tight">17.500 kr.</span>
                    </div>
                  </div>
                  
                  <p className="text-sm text-[#3B352F]/70 mb-6 leading-relaxed">
                    Forløb giver jer mulighed for at arbejde mere langsigtet og integrere de nye mønstre dybt i hverdagen.
                  </p>
                  
                  <ul className="space-y-3 mb-8 border-t border-[#E8DDCE]/50 pt-6">
                    <li className="flex items-center gap-2.5 text-xs text-[#3B352F]/80">
                      <i className="fa-light fa-circle-check text-[#193f10] text-sm shrink-0" /> Større kontinuitet
                    </li>
                    <li className="flex items-center gap-2.5 text-xs text-[#3B352F]/80">
                      <i className="fa-light fa-circle-check text-[#193f10] text-sm shrink-0" /> Spar op til 2.000 kr.
                    </li>
                    <li className="flex items-center gap-2.5 text-xs text-[#3B352F]/80">
                      <i className="fa-light fa-circle-check text-[#193f10] text-sm shrink-0" /> Fleksibel planlægning
                    </li>
                  </ul>
                </div>
                
                <Link 
                  to="/bestil-tid" 
                  className="w-full py-3.5 bg-[#FAF7F2] text-[#193f10] border border-[#E8DDCE] font-bold rounded-md hover:bg-[#E8DDCE]/50 text-center transition-all duration-200 text-sm"
                >
                  Vælg forløb
                </Link>
              </div>

            </div>

            {/* Note about individual therapy */}
            <div className="max-w-5xl mx-auto mt-10 text-center">
              <p className="text-xs text-[#3B352F]/70 leading-relaxed max-w-3xl mx-auto">
                <strong>Bemærk:</strong> Hvis forløbet efter første relationssession fortsætter individuelt (fx hvis kun den ene part ønsker at fortsætte i et individuelt terapeutisk forløb), gælder prisen for almindelig individuel terapi: <strong>1.800 kr.</strong> pr. session (1.500 kr. med lægehenvisning).
              </p>
            </div>
          </div>
        </section>

        {/* SÆRLIGE BEHANDLINGSFORLØB */}
        <section className="py-20 bg-white border-y border-[#E8DDCE]/50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-[#862e0a] font-bold text-xs uppercase tracking-widest block mb-3">
                Målrettet Behandling
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-[#193f10] mb-4">
                Særlige behandlingsforløb
              </h2>
              <p className="text-base text-[#3B352F]/80 leading-relaxed">
                Nogle klienter ønsker hjælp til en bestemt problemstilling. Her kan du se eksempler på specialiserede forløb, som tilpasses den enkelte. Forløbene kan som udgangspunkt vælges som 5 eller 10 sessioner, medmindre andet er angivet.
              </p>
            </div>

            {/* Accordion List */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-6xl mx-auto">
              {specialties.map((item, index) => {
                const isOpen = activeSpecialty === index;
                return (
                  <div 
                    key={index} 
                    className={`border rounded-xl transition-all duration-300 overflow-hidden ${
                      isOpen 
                        ? 'border-[#193f10] bg-[#FAF7F2] shadow-sm' 
                        : 'border-[#E8DDCE] bg-white hover:border-[#193f10]/40'
                    }`}
                  >
                    {/* Header */}
                    <button 
                      onClick={() => toggleSpecialty(index)}
                      className="w-full px-6 py-5 flex justify-between items-center text-left gap-4"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                          isOpen ? 'bg-[#193f10] text-white' : 'bg-[#FAF7F2] text-[#193f10]'
                        }`}>
                          <i className={`${item.icon} text-lg`} />
                        </div>
                        <div>
                          <h3 className="font-serif font-bold text-base md:text-lg text-[#193f10]">
                            {item.title}
                          </h3>
                          <p className="text-xs text-[#3B352F]/60 md:hidden mt-0.5">
                            {item.price}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        <span className="hidden md:inline text-sm font-bold text-[#193f10] bg-white px-3 py-1 rounded-full border border-[#E8DDCE]">
                          {item.price}
                        </span>
                        <i className={`fa-light fa-chevron-down transition-transform duration-300 text-[#193f10] ${
                          isOpen ? 'rotate-180' : ''
                        }`} />
                      </div>
                    </button>

                    {/* Content */}
                    <div 
                      className={`transition-all duration-300 ease-in-out ${
                        isOpen ? 'max-h-[1000px] border-t border-[#E8DDCE]/60' : 'max-h-0'
                      }`}
                    >
                      <div className="p-6 md:p-8 space-y-4 text-sm md:text-base text-[#3B352F]/80 leading-relaxed">
                        <p className="font-medium text-[#193f10]">
                          {item.description}
                        </p>
                        <div className="whitespace-pre-line text-[#3B352F]/75 border-l-2 border-[#193f10]/20 pl-4 py-1">
                          {item.details}
                        </div>
                        <div className="pt-4 flex flex-col sm:flex-row gap-4 items-center justify-between">
                          <span className="text-xs text-[#3B352F]/50">
                            * Forløbet tilpasses altid individuelt efter dine behov.
                          </span>
                          <div className="flex gap-3 w-full sm:w-auto">
                            <Link 
                              to="/bestil-tid" 
                              className="w-full sm:w-auto px-5 py-2.5 bg-[#193f10] text-white text-xs font-bold rounded-md hover:bg-[#193f10]/90 text-center transition-all"
                            >
                              Book forløb
                            </Link>
                            <Link 
                              to="/kontakt" 
                              className="w-full sm:w-auto px-5 py-2.5 bg-[#E8DDCE] text-[#193f10] text-xs font-bold rounded-md hover:bg-[#E8DDCE]/80 text-center transition-all"
                            >
                              Spørg Lars
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* TILVALG OG OPFØLGNING */}
        <section className="py-20 md:py-28">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-[#193f10] font-bold text-xs uppercase tracking-widest block mb-3">
                Fleksible Muligheder
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-[#193f10] mb-4">
                Tilvalg og opfølgning
              </h2>
              <p className="text-base text-[#3B352F]/80 leading-relaxed">
                Korte samtaler og opfølgninger kan bruges til justering, afklaring eller støtte mellem sessioner.
              </p>
            </div>

            {/* Grid of 4 cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              
              {/* Option 1 */}
              <div className="bg-white border border-[#E8DDCE] rounded-xl p-6 flex flex-col justify-between hover:border-[#193f10]/40 hover:shadow-md transition-all duration-300">
                <div>
                  <h4 className="font-serif font-bold text-[#193f10] text-base mb-1">
                    Kort opfølgende samtale
                  </h4>
                  <p className="text-xs text-[#3B352F]/50 uppercase tracking-wider mb-3">20 min. / 700 kr.</p>
                  <p className="text-xs text-[#3B352F]/70 leading-relaxed mb-6">
                    Til dig, der allerede er i forløb eller tidligere har været i forløb og ønsker en kort afklaring eller justering.
                  </p>
                </div>
                <Link to="/bestil-tid" className="text-xs font-bold text-[#193f10] hover:underline flex items-center gap-1 mt-auto">
                  Book samtale <i className="fa-light fa-arrow-right text-[10px]" />
                </Link>
              </div>

              {/* Option 2 */}
              <div className="bg-white border border-[#E8DDCE] rounded-xl p-6 flex flex-col justify-between hover:border-[#193f10]/40 hover:shadow-md transition-all duration-300">
                <div>
                  <h4 className="font-serif font-bold text-[#193f10] text-base mb-1">
                    Telefonisk opfølgning
                  </h4>
                  <p className="text-xs text-[#3B352F]/50 uppercase tracking-wider mb-3">15 min. / 500 kr.</p>
                  <p className="text-xs text-[#3B352F]/70 leading-relaxed mb-6">
                    En kort planlagt telefonsamtale til afklaring eller opfølgning.
                  </p>
                </div>
                <Link to="/bestil-tid" className="text-xs font-bold text-[#193f10] hover:underline flex items-center gap-1 mt-auto">
                  Book opringning <i className="fa-light fa-arrow-right text-[10px]" />
                </Link>
              </div>

              {/* Option 3 */}
              <div className="bg-white border border-[#E8DDCE] rounded-xl p-6 flex flex-col justify-between hover:border-[#193f10]/40 hover:shadow-md transition-all duration-300">
                <div>
                  <h4 className="font-serif font-bold text-[#193f10] text-base mb-1">
                    Ekstra støttesamtale
                  </h4>
                  <p className="text-xs text-[#3B352F]/50 uppercase tracking-wider mb-3">30 min. / 900 kr.</p>
                  <p className="text-xs text-[#3B352F]/70 leading-relaxed mb-6">
                    Til dig, der er i forløb og har brug for en ekstra samtale mellem to planlagte sessioner.
                  </p>
                </div>
                <Link to="/bestil-tid" className="text-xs font-bold text-[#193f10] hover:underline flex items-center gap-1 mt-auto">
                  Book støtte <i className="fa-light fa-arrow-right text-[10px]" />
                </Link>
              </div>

              {/* Option 4 */}
              <div className="bg-white border border-[#E8DDCE] rounded-xl p-6 flex flex-col justify-between hover:border-[#193f10]/40 hover:shadow-md transition-all duration-300">
                <div>
                  <h4 className="font-serif font-bold text-[#193f10] text-base mb-1">
                    Online-session
                  </h4>
                  <p className="text-xs text-[#3B352F]/50 uppercase tracking-wider mb-3">50 min. / 1.800 kr.</p>
                  <p className="text-xs text-[#3B352F]/70 leading-relaxed mb-6">
                    En session via video med samme faglige indhold som en fysisk session.
                  </p>
                </div>
                <Link to="/bestil-tid" className="text-xs font-bold text-[#193f10] hover:underline flex items-center gap-1 mt-auto">
                  Book online <i className="fa-light fa-arrow-right text-[10px]" />
                </Link>
              </div>

            </div>
          </div>
        </section>

        {/* ERHVERVSCOACHING */}
        <section className="py-20 bg-[#193f10] text-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-[#E8DDCE] font-bold text-xs uppercase tracking-widest block mb-3">
                Erhvervscoaching
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-white mb-4">
                Priser for erhvervscoaching
              </h2>
              <p className="text-base text-[#FAF7F2]/90 leading-relaxed">
                Erhvervscoaching er adskilt fra private priser. Alle priser i denne sektion er ekskl. moms.
              </p>
            </div>

            {/* Erhverv Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch max-w-5xl mx-auto mb-10">
              
              {/* Card 1: Enkelt session */}
              <div className="bg-[#FAF7F2] border border-[#E8DDCE] rounded-2xl p-8 flex flex-col hover:shadow-xl transition-all duration-300 text-[#3B352F]">
                <div>
                  <span className="text-xs font-bold text-[#B89B5E] uppercase tracking-wider block mb-1">
                    Erhvervscoaching
                  </span>
                  <h3 className="text-xl font-serif text-[#193f10] font-bold mb-1">Enkelt session</h3>
                  <p className="text-xs text-[#3B352F]/60 mb-4">ekskl. moms · 50 minutter</p>
                  
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-3xl font-bold text-[#193f10] tracking-tight">1.560</span>
                    <span className="text-xs text-[#3B352F]/60 font-medium">kr. ekskl. moms</span>
                  </div>
                  
                  <p className="text-xs text-[#3B352F]/70 leading-relaxed">
                    Til arbejdsliv, selvstændighed, stresshåndtering og beslutningskraft.
                  </p>
                </div>
              </div>

              {/* Card 2: 5 sessioner */}
              <div className="bg-[#FAF7F2] border border-[#E8DDCE] rounded-2xl p-8 flex flex-col hover:shadow-xl transition-all duration-300 text-[#3B352F]">
                <div>
                  <span className="text-xs font-bold text-[#B89B5E] uppercase tracking-wider block mb-1">
                    Klippekort erhverv
                  </span>
                  <h3 className="text-xl font-serif text-[#193f10] font-bold mb-1">5 sessioner</h3>
                  <p className="text-xs text-[#3B352F]/60 mb-4">ekskl. moms · 5 x 50 minutter</p>
                  
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-3xl font-bold text-[#193f10] tracking-tight">7.400</span>
                    <span className="text-xs text-[#3B352F]/60 font-medium">kr. ekskl. moms</span>
                  </div>
                  
                  <p className="text-xs text-[#3B352F]/70 leading-relaxed">
                    Til et kortere, målrettet erhvervsrettet coachingforløb.
                  </p>
                </div>
              </div>

              {/* Card 3: 10 sessioner */}
              <div className="bg-[#FAF7F2] border border-[#E8DDCE] rounded-2xl p-8 flex flex-col hover:shadow-xl transition-all duration-300 text-[#3B352F]">
                <div>
                  <span className="text-xs font-bold text-[#B89B5E] uppercase tracking-wider block mb-1">
                    Klippekort erhverv
                  </span>
                  <h3 className="text-xl font-serif text-[#193f10] font-bold mb-1">10 sessioner</h3>
                  <p className="text-xs text-[#3B352F]/60 mb-4">ekskl. moms · 10 x 50 minutter</p>
                  
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-3xl font-bold text-[#193f10] tracking-tight">14.000</span>
                    <span className="text-xs text-[#3B352F]/60 font-medium">kr. ekskl. moms</span>
                  </div>
                  
                  <p className="text-xs text-[#3B352F]/70 leading-relaxed">
                    Til et længere forløb med struktur, opfølgning og kontinuitet.
                  </p>
                </div>
              </div>

            </div>

            {/* Gold CTA Button */}
            <div className="text-center mb-12">
              <Link 
                to="/kontakt" 
                className="inline-flex items-center bg-[#B89B5E] text-white px-8 py-4 rounded-md font-bold hover:bg-[#B89B5E]/90 transition-all shadow-lg hover:shadow-xl"
              >
                Kontakt mig om erhvervscoaching
              </Link>
            </div>

            {/* Erhverv info text */}
            <div className="max-w-4xl mx-auto bg-white/10 border border-white/20 rounded-xl p-6 text-xs text-[#FAF7F2]/90 leading-relaxed">
              <i className="fa-light fa-circle-info text-[#E8DDCE] mr-1.5" />
              Der kan udstedes faktura til virksomheden, hvis forløbet aftales som erhvervsrettet coaching. Du skal selv afklare med revisor eller bogholder, om udgiften kan fratrækkes.
            </div>
          </div>
        </section>

        {/* PRAKTISKE RAMMER */}
        <section className="py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-[#193f10] font-bold text-xs uppercase tracking-widest block mb-3">
                Information
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-[#193f10] mb-4">
                Praktiske rammer
              </h2>
              <p className="text-base text-[#3B352F]/80 leading-relaxed">
                Her finder du de vigtigste oplysninger om afbud, betaling, notater og åbningstider.
              </p>
            </div>

            {/* Grid of practical info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              
              {/* Box 1: Betaling før session */}
              <div className="bg-white border border-[#E8DDCE] rounded-xl p-6">
                <div className="w-10 h-10 rounded-full bg-[#193f10]/5 text-[#193f10] flex items-center justify-center mb-4">
                  <i className="fa-light fa-credit-card text-lg" />
                </div>
                <h4 className="font-serif font-bold text-[#193f10] text-base mb-2">Betaling før session</h4>
                <p className="text-xs text-[#3B352F]/75 leading-relaxed">
                  Betaling sker altid <strong>inden sessionen starter.</strong> Dette gælder både enkeltsessioner, forløb, klippekort og online-sessioner.
                </p>
              </div>

              {/* Box 2: Afbud og udeblivelse */}
              <div className="bg-white border border-[#E8DDCE] rounded-xl p-6">
                <div className="w-10 h-10 rounded-full bg-[#193f10]/5 text-[#193f10] flex items-center justify-center mb-4">
                  <i className="fa-light fa-calendar-xmark text-lg" />
                </div>
                <h4 className="font-serif font-bold text-[#193f10] text-base mb-2">Afbud og udeblivelse</h4>
                <p className="text-xs text-[#3B352F]/75 leading-relaxed">
                  Ved afbud senere end 24 timer før den aftalte tid, eller ved udeblivelse, betales sessionen som afholdt. Har du klippekort, trækkes der ét klip.
                </p>
              </div>

              {/* Box 3: Administrationsgebyr */}
              <div className="bg-white border border-[#E8DDCE] rounded-xl p-6">
                <div className="w-10 h-10 rounded-full bg-[#193f10]/5 text-[#193f10] flex items-center justify-center mb-4">
                  <i className="fa-light fa-file-invoice-dollar text-lg" />
                </div>
                <h4 className="font-serif font-bold text-[#193f10] text-base mb-2">Administrationsgebyr</h4>
                <p className="text-xs text-[#3B352F]/75 leading-relaxed">
                  <strong>500 kr.</strong> Anvendes ved ekstra administrativt arbejde, som ligger ud over almindelig behandling.
                </p>
              </div>

              {/* Box 4: Kort behandlingsnotat */}
              <div className="bg-white border border-[#E8DDCE] rounded-xl p-6">
                <div className="w-10 h-10 rounded-full bg-[#193f10]/5 text-[#193f10] flex items-center justify-center mb-4">
                  <i className="fa-light fa-file-lines text-lg" />
                </div>
                <h4 className="font-serif font-bold text-[#193f10] text-base mb-2">Kort behandlingsnotat</h4>
                <p className="text-xs text-[#3B352F]/75 leading-relaxed">
                  <strong>700 kr.</strong> Et kort, neutralt skriftligt notat med opsummering af behandlingsforløb, fremmøde eller overordnede temaer.
                </p>
              </div>

              {/* Box 5: Udvidet behandlingsopsummering */}
              <div className="bg-white border border-[#E8DDCE] rounded-xl p-6">
                <div className="w-10 h-10 rounded-full bg-[#193f10]/5 text-[#193f10] flex items-center justify-center mb-4">
                  <i className="fa-light fa-file-medical text-lg" />
                </div>
                <h4 className="font-serif font-bold text-[#193f10] text-base mb-2">Udvidet behandlingsopsummering</h4>
                <p className="text-xs text-[#3B352F]/75 leading-relaxed">
                  <strong>Fra 1.800 kr.</strong> En længere skriftlig opsummering af et behandlingsforløb. Udarbejdes kun efter aftale.
                </p>
              </div>

              {/* Box 6: Betalingsmuligheder */}
              <div className="bg-white border border-[#E8DDCE] rounded-xl p-6">
                <div className="w-10 h-10 rounded-full bg-[#193f10]/5 text-[#193f10] flex items-center justify-center mb-4">
                  <i className="fa-light fa-wallet text-lg" />
                </div>
                <h4 className="font-serif font-bold text-[#193f10] text-base mb-2">Betalingsmuligheder</h4>
                <p className="text-xs text-[#3B352F]/75 leading-relaxed">
                  Betaling kan ske via MobilePay eller bankoverførsel. Ved forløb aftales betaling tydeligt på forhånd.
                </p>
              </div>

              {/* Box 7: Tilskud */}
              <div className="bg-white border border-[#E8DDCE] rounded-xl p-6">
                <div className="w-10 h-10 rounded-full bg-[#193f10]/5 text-[#193f10] flex items-center justify-center mb-4">
                  <i className="fa-light fa-shield-halved text-lg" />
                </div>
                <h4 className="font-serif font-bold text-[#193f10] text-base mb-2">Tilskud</h4>
                <p className="text-xs text-[#3B352F]/75 leading-relaxed">
                  Nogle sundhedsforsikringer yder tilskud til psykoterapeutisk behandling hos en Psykoterapeut MPF. Kontakt din forsikring, hvis du vil undersøge dine muligheder.
                </p>
              </div>

              {/* Box 8: Åbningstider */}
              <div className="bg-white border border-[#E8DDCE] rounded-xl p-6">
                <div className="w-10 h-10 rounded-full bg-[#193f10]/5 text-[#193f10] flex items-center justify-center mb-4">
                  <i className="fa-light fa-clock text-lg" />
                </div>
                <h4 className="font-serif font-bold text-[#193f10] text-base mb-2">Åbningstider</h4>
                <p className="text-xs text-[#3B352F]/75 leading-relaxed">
                  <strong>Mandag-fredag:</strong> 09.00-16.00<br />
                  <strong>Lørdag-søndag:</strong> Lukket
                </p>
              </div>

              {/* Box 9: Adresse */}
              <div className="bg-white border border-[#E8DDCE] rounded-xl p-6">
                <div className="w-10 h-10 rounded-full bg-[#193f10]/5 text-[#193f10] flex items-center justify-center mb-4">
                  <i className="fa-light fa-location-dot text-lg" />
                </div>
                <h4 className="font-serif font-bold text-[#193f10] text-base mb-2">Adresse</h4>
                <p className="text-xs text-[#3B352F]/75 leading-relaxed">
                  <strong>Core Hypnose</strong><br />
                  Valby Tingsted 7<br />
                  2500 Valby
                </p>
              </div>

            </div>
          </div>
        </section>

        {/* TILSKUD OG ØKONOMISKE MULIGHEDER */}
        <section className="py-20 bg-white border-y border-[#E8DDCE]/50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-[#862e0a] font-bold text-xs uppercase tracking-widest block mb-3">
                Økonomi & tilskud
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-[#193f10] mb-4">
                Tilskud og økonomiske muligheder
              </h2>
              <p className="text-base text-[#3B352F]/80 leading-relaxed">
                Nogle klienter kan i særlige tilfælde have mulighed for støtte, rabat eller økonomisk hjælp til behandlingen. Her får du et overblik over mulighederne:
              </p>
            </div>

            {/* Visual Overview Grid of 6 Infoboxes */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto mb-16">
              
              {/* Box 1: Core-rabat */}
              <button 
                onClick={() => { setActiveSupport(0); const el = document.getElementById('support-details'); if(el) el.scrollIntoView({ behavior: 'smooth' }); }}
                className="bg-[#FAF7F2] border border-[#E8DDCE] rounded-2xl p-6 text-left hover:border-[#193f10] hover:shadow-md transition-all group flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-10 h-10 rounded-full bg-[#193f10]/10 text-[#193f10] flex items-center justify-center group-hover:bg-[#193f10] group-hover:text-white transition-colors">
                      <i className="fa-light fa-file-medical text-lg" />
                    </div>
                    <span className="text-[11px] font-bold text-white bg-[#193f10] px-2.5 py-1 rounded-full">
                      300 kr. rabat
                    </span>
                  </div>
                  <h3 className="font-serif font-bold text-base text-[#193f10] mb-2">
                    Core-rabat ved lægehenvisning
                  </h3>
                  <p className="text-xs text-[#3B352F]/75 leading-relaxed mb-4">
                    Reducerer individuel session til 1.500 kr. ved fremvisning af lægehenvisning.
                  </p>
                </div>
                <span className="text-xs font-semibold text-[#193f10] flex items-center gap-1 group-hover:underline mt-auto">
                  Læs detaljer <i className="fa-light fa-chevron-down text-[10px]" />
                </span>
              </button>

              {/* Box 2: Privat sundhedsforsikring */}
              <button 
                onClick={() => { setActiveSupport(1); const el = document.getElementById('support-details'); if(el) el.scrollIntoView({ behavior: 'smooth' }); }}
                className="bg-[#FAF7F2] border border-[#E8DDCE] rounded-2xl p-6 text-left hover:border-[#193f10] hover:shadow-md transition-all group flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-10 h-10 rounded-full bg-[#193f10]/10 text-[#193f10] flex items-center justify-center group-hover:bg-[#193f10] group-hover:text-white transition-colors">
                      <i className="fa-light fa-shield-heart text-lg" />
                    </div>
                    <span className="text-[11px] font-bold text-[#193f10] bg-[#E8DDCE] px-2.5 py-1 rounded-full">
                      Forsikring
                    </span>
                  </div>
                  <h3 className="font-serif font-bold text-base text-[#193f10] mb-2">
                    Privat sundhedsforsikring
                  </h3>
                  <p className="text-xs text-[#3B352F]/75 leading-relaxed mb-4">
                    Nogle sundhedsforsikringer yder hel eller delvis dækning til forløbet via din police.
                  </p>
                </div>
                <span className="text-xs font-semibold text-[#193f10] flex items-center gap-1 group-hover:underline mt-auto">
                  Læs detaljer <i className="fa-light fa-chevron-down text-[10px]" />
                </span>
              </button>

              {/* Box 3: Kommunal støtte */}
              <button 
                onClick={() => { setActiveSupport(2); const el = document.getElementById('support-details'); if(el) el.scrollIntoView({ behavior: 'smooth' }); }}
                className="bg-[#FAF7F2] border border-[#E8DDCE] rounded-2xl p-6 text-left hover:border-[#193f10] hover:shadow-md transition-all group flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-10 h-10 rounded-full bg-[#193f10]/10 text-[#193f10] flex items-center justify-center group-hover:bg-[#193f10] group-hover:text-white transition-colors">
                      <i className="fa-light fa-landmark text-lg" />
                    </div>
                    <span className="text-[11px] font-bold text-[#193f10] bg-[#E8DDCE] px-2.5 py-1 rounded-full">
                      Lovgivning
                    </span>
                  </div>
                  <h3 className="font-serif font-bold text-base text-[#193f10] mb-2">
                    Kommunal støtte
                  </h3>
                  <p className="text-xs text-[#3B352F]/75 leading-relaxed mb-4">
                    Mulighed for støtte efter Aktivlovens § 82 eller Servicelovens § 102 ved særlige behov.
                  </p>
                </div>
                <span className="text-xs font-semibold text-[#193f10] flex items-center gap-1 group-hover:underline mt-auto">
                  Læs detaljer <i className="fa-light fa-chevron-down text-[10px]" />
                </span>
              </button>

              {/* Box 4: Pensionist, studerende el. sygemeldt */}
              <button 
                onClick={() => { setActiveSupport(3); const el = document.getElementById('support-details'); if(el) el.scrollIntoView({ behavior: 'smooth' }); }}
                className="bg-[#FAF7F2] border border-[#E8DDCE] rounded-2xl p-6 text-left hover:border-[#193f10] hover:shadow-md transition-all group flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-10 h-10 rounded-full bg-[#193f10]/10 text-[#193f10] flex items-center justify-center group-hover:bg-[#193f10] group-hover:text-white transition-colors">
                      <i className="fa-light fa-graduation-cap text-lg" />
                    </div>
                    <span className="text-[11px] font-bold text-[#862e0a] bg-[#862e0a]/10 px-2.5 py-1 rounded-full">
                      25% rabat
                    </span>
                  </div>
                  <h3 className="font-serif font-bold text-base text-[#193f10] mb-2">
                    Pensionist, studerende eller sygemeldt
                  </h3>
                  <p className="text-xs text-[#3B352F]/75 leading-relaxed mb-4">
                    Individuel session til 1.350 kr. ved fremvisning af relevant dokumentation.
                  </p>
                </div>
                <span className="text-xs font-semibold text-[#193f10] flex items-center gap-1 group-hover:underline mt-auto">
                  Læs detaljer <i className="fa-light fa-chevron-down text-[10px]" />
                </span>
              </button>

              {/* Box 5: Faktura til virksomhed */}
              <button 
                onClick={() => { setActiveSupport(4); const el = document.getElementById('support-details'); if(el) el.scrollIntoView({ behavior: 'smooth' }); }}
                className="bg-[#FAF7F2] border border-[#E8DDCE] rounded-2xl p-6 text-left hover:border-[#193f10] hover:shadow-md transition-all group flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-10 h-10 rounded-full bg-[#193f10]/10 text-[#193f10] flex items-center justify-center group-hover:bg-[#193f10] group-hover:text-white transition-colors">
                      <i className="fa-light fa-file-invoice-dollar text-lg" />
                    </div>
                    <span className="text-[11px] font-bold text-[#193f10] bg-[#E8DDCE] px-2.5 py-1 rounded-full">
                      Erhverv
                    </span>
                  </div>
                  <h3 className="font-serif font-bold text-base text-[#193f10] mb-2">
                    Faktura til virksomhed
                  </h3>
                  <p className="text-xs text-[#3B352F]/75 leading-relaxed mb-4">
                    Erhvervscoaching kan faktureres til virksomhed eller arbejdsgiver som driftsomkostning.
                  </p>
                </div>
                <span className="text-xs font-semibold text-[#193f10] flex items-center gap-1 group-hover:underline mt-auto">
                  Læs detaljer <i className="fa-light fa-chevron-down text-[10px]" />
                </span>
              </button>

              {/* Box 6: Et prisvenligt første skridt */}
              <button 
                onClick={() => { setActiveSupport(5); const el = document.getElementById('support-details'); if(el) el.scrollIntoView({ behavior: 'smooth' }); }}
                className="bg-[#FAF7F2] border-2 border-[#b59b5a] rounded-2xl p-6 text-left hover:border-[#193f10] hover:shadow-md transition-all group flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-10 h-10 rounded-full bg-[#b59b5a]/20 text-[#123F13] flex items-center justify-center group-hover:bg-[#b59b5a] group-hover:text-[#123F13] transition-colors">
                      <i className="fa-light fa-seedling text-lg" />
                    </div>
                    <span className="text-[11px] font-bold text-[#123F13] bg-[#b59b5a] px-2.5 py-1 rounded-full">
                      Kun 375 kr.
                    </span>
                  </div>
                  <h3 className="font-serif font-bold text-base text-[#193f10] mb-2">
                    Et prisvenligt første skridt
                  </h3>
                  <p className="text-xs text-[#3B352F]/75 leading-relaxed mb-4">
                    Første behandlingssession (50 min.) giver afklaring og behandlingsplan uden binding.
                  </p>
                </div>
                <span className="text-xs font-semibold text-[#193f10] flex items-center gap-1 group-hover:underline mt-auto">
                  Læs detaljer <i className="fa-light fa-chevron-down text-[10px]" />
                </span>
              </button>

            </div>

            {/* Info Box: Undersøg dine muligheder */}
            <div id="support-details" className="max-w-5xl mx-auto bg-[#FAF7F2] border border-[#E8DDCE] rounded-2xl p-6 md:p-8 mb-8 flex items-start gap-5">
              <div className="w-12 h-12 rounded-full bg-[#193f10]/10 flex items-center justify-center text-[#193f10] shrink-0">
                <i className="fa-light fa-circle-question text-xl" />
              </div>
              <div>
                <h3 className="text-lg font-serif font-bold text-[#193f10] mb-2">Undersøg dine muligheder</h3>
                <p className="text-sm text-[#3B352F]/80 leading-relaxed">
                  Core Hypnose kan udstede faktura og dokumentation for betalte sessioner. Eventuel støtte, dækning eller refusion afhænger dog altid af den konkrete ordning og din individuelle situation. Klik på emnerne nedenfor for at læse mere.
                </p>
              </div>
            </div>

            {/* Detailed Accordion List */}
            <div className="max-w-5xl mx-auto space-y-4">
              
              {/* Accordion 1: Core-rabat ved lægehenvisning */}
              <div 
                className={`border rounded-xl transition-all duration-300 overflow-hidden ${
                  activeSupport === 0 
                    ? 'border-[#193f10] bg-[#FAF7F2] shadow-sm' 
                    : 'border-[#E8DDCE] bg-white hover:border-[#193f10]/40'
                }`}
              >
                {/* Header */}
                <button 
                  onClick={() => toggleSupport(0)}
                  className="w-full px-6 py-5 flex justify-between items-center text-left gap-4"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                      activeSupport === 0 ? 'bg-[#193f10] text-white' : 'bg-[#FAF7F2] text-[#193f10]'
                    }`}>
                      <i className="fa-light fa-file-medical text-lg" />
                    </div>
                    <h3 className="font-serif font-bold text-base md:text-lg text-[#193f10]">
                      Core-rabat ved lægehenvisning
                    </h3>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <span className="text-xs font-bold text-white bg-[#193f10] px-3 py-1 rounded-full">
                      300 kr. rabat
                    </span>
                    <i className={`fa-light fa-chevron-down transition-transform duration-300 text-[#193f10] ${
                      activeSupport === 0 ? 'rotate-180' : ''
                    }`} />
                  </div>
                </button>

                {/* Content */}
                <div 
                  className={`transition-all duration-300 ease-in-out ${
                    activeSupport === 0 ? 'max-h-[1000px] border-t border-[#E8DDCE]/60' : 'max-h-0'
                  }`}
                >
                  <div className="p-6 md:p-8 space-y-4 text-sm text-[#3B352F]/80 leading-relaxed">
                    <p>
                      Har du en lægehenvisning, tilbyder Core Hypnose en privat Core-rabat på 300 kr. på en almindelig individuel session. Det betyder, at en individuel session på 50 minutter koster 1.500 kr. i stedet for 1.800 kr.
                    </p>
                    <p>
                      Til sammenligning viser Sygeforsikringen “danmark” blandt andet 200 kr. i tilskud til individuel/topersoners psykologkonsultation med offentligt tilskud hos autoriseret psykolog. Core-rabatten hos Core Hypnose er 300 kr. og dermed 100 kr. højere end dette sammenligningsbeløb.
                    </p>
                    <p>
                      Rabatten er Core Hypnoses egen rabat. Den er ikke et offentligt tilskud og ikke et tilskud fra Sygeforsikringen “danmark”. Du behøver derfor ikke være medlem af “danmark” for at få Core-rabatten. Du skal blot kunne fremvise en lægehenvisning.
                    </p>
                    <div className="pt-2">
                      <a 
                        href="https://www.sygeforsikring.dk" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#193f10] hover:underline"
                      >
                        Se de aktuelle regler og satser hos Sygeforsikringen “danmark” <i className="fa-light fa-arrow-up-right-from-square text-[10px]" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Accordion 2: Privat sundhedsforsikring */}
              <div 
                className={`border rounded-xl transition-all duration-300 overflow-hidden ${
                  activeSupport === 1 
                    ? 'border-[#193f10] bg-[#FAF7F2] shadow-sm' 
                    : 'border-[#E8DDCE] bg-white hover:border-[#193f10]/40'
                }`}
              >
                {/* Header */}
                <button 
                  onClick={() => toggleSupport(1)}
                  className="w-full px-6 py-5 flex justify-between items-center text-left gap-4"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                      activeSupport === 1 ? 'bg-[#193f10] text-white' : 'bg-[#FAF7F2] text-[#193f10]'
                    }`}>
                      <i className="fa-light fa-shield-heart text-lg" />
                    </div>
                    <h3 className="font-serif font-bold text-base md:text-lg text-[#193f10]">
                      Privat sundhedsforsikring
                    </h3>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <span className="text-xs font-bold text-[#193f10] bg-[#E8DDCE] px-3 py-1 rounded-full">
                      Kontakt din forsikring
                    </span>
                    <i className={`fa-light fa-chevron-down transition-transform duration-300 text-[#193f10] ${
                      activeSupport === 1 ? 'rotate-180' : ''
                    }`} />
                  </div>
                </button>

                {/* Content */}
                <div 
                  className={`transition-all duration-300 ease-in-out ${
                    activeSupport === 1 ? 'max-h-[1000px] border-t border-[#E8DDCE]/60' : 'max-h-0'
                  }`}
                >
                  <div className="p-6 md:p-8 space-y-4 text-sm text-[#3B352F]/80 leading-relaxed">
                    <p>
                      Nogle sundhedsforsikringer dækker helt eller delvist samtaler eller behandlingsforløb hos en Psykoterapeut MPF. Dækningen afhænger af dit forsikringsselskab og din police. Det anbefales at kontakte dit forsikringsselskab direkte for at høre, om du kan få dækning til et forløb hos Lars Nissen Corell – Psykoterapeut MPF.
                    </p>
                    <div className="pt-2">
                      <Link 
                        to="/kontakt" 
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#193f10] hover:underline"
                      >
                        Kontakt mig for dokumentation <i className="fa-light fa-arrow-right text-[10px]" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* Accordion 3: Kommunal støtte */}
              <div 
                className={`border rounded-xl transition-all duration-300 overflow-hidden ${
                  activeSupport === 2 
                    ? 'border-[#193f10] bg-[#FAF7F2] shadow-sm' 
                    : 'border-[#E8DDCE] bg-white hover:border-[#193f10]/40'
                }`}
              >
                {/* Header */}
                <button 
                  onClick={() => toggleSupport(2)}
                  className="w-full px-6 py-5 flex justify-between items-center text-left gap-4"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                      activeSupport === 2 ? 'bg-[#193f10] text-white' : 'bg-[#FAF7F2] text-[#193f10]'
                    }`}>
                      <i className="fa-light fa-landmark text-lg" />
                    </div>
                    <h3 className="font-serif font-bold text-base md:text-lg text-[#193f10]">
                      Kommunal støtte
                    </h3>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <span className="text-xs font-bold text-[#193f10] bg-[#E8DDCE] px-3 py-1 rounded-full">
                      Aktivloven § 82 / Serviceloven § 102
                    </span>
                    <i className={`fa-light fa-chevron-down transition-transform duration-300 text-[#193f10] ${
                      activeSupport === 2 ? 'rotate-180' : ''
                    }`} />
                  </div>
                </button>

                {/* Content */}
                <div 
                  className={`transition-all duration-300 ease-in-out ${
                    activeSupport === 2 ? 'max-h-[1000px] border-t border-[#E8DDCE]/60' : 'max-h-0'
                  }`}
                >
                  <div className="p-6 md:p-8 space-y-4 text-sm text-[#3B352F]/80 leading-relaxed">
                    <p>
                      I nogle tilfælde kan der være mulighed for støtte via kommunen. Aktivlovens § 82 omhandler mulighed for hjælp til udgifter til sygebehandling i særlige tilfælde, hvis man ikke selv har økonomisk mulighed for at betale. Servicelovens § 102 omhandler mulighed for behandlingsmæssige tilbud til borgere med betydelig og varigt nedsat fysisk eller psykisk funktionsevne eller særlige sociale problemer.
                    </p>
                    <p>
                      Det er altid kommunen, der vurderer, om betingelserne er opfyldt i det enkelte tilfælde.
                    </p>
                    <div className="pt-2 flex flex-col sm:flex-row gap-4">
                      <a 
                        href="https://www.retsinformation.dk" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#193f10] hover:underline"
                      >
                        Læs om Aktivloven på Retsinformation <i className="fa-light fa-arrow-up-right-from-square text-[10px]" />
                      </a>
                      <a 
                        href="https://www.retsinformation.dk" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#193f10] hover:underline"
                      >
                        Læs om Serviceloven på Retsinformation <i className="fa-light fa-arrow-up-right-from-square text-[10px]" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Accordion 4: Pensionist, studerende eller sygemeldt */}
              <div 
                className={`border rounded-xl transition-all duration-300 overflow-hidden ${
                  activeSupport === 3 
                    ? 'border-[#193f10] bg-[#FAF7F2] shadow-sm' 
                    : 'border-[#E8DDCE] bg-white hover:border-[#193f10]/40'
                }`}
              >
                {/* Header */}
                <button 
                  onClick={() => toggleSupport(3)}
                  className="w-full px-6 py-5 flex justify-between items-center text-left gap-4"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                      activeSupport === 3 ? 'bg-[#193f10] text-white' : 'bg-[#FAF7F2] text-[#193f10]'
                    }`}>
                      <i className="fa-light fa-graduation-cap text-lg" />
                    </div>
                    <h3 className="font-serif font-bold text-base md:text-lg text-[#193f10]">
                      Pensionist, studerende eller sygemeldt
                    </h3>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <span className="text-xs font-bold text-[#193f10] bg-[#E8DDCE] px-3 py-1 rounded-full">
                      Mulighed for rabat
                    </span>
                    <i className={`fa-light fa-chevron-down transition-transform duration-300 text-[#193f10] ${
                      activeSupport === 3 ? 'rotate-180' : ''
                    }`} />
                  </div>
                </button>

                {/* Content */}
                <div 
                  className={`transition-all duration-300 ease-in-out ${
                    activeSupport === 3 ? 'max-h-[1000px] border-t border-[#E8DDCE]/60' : 'max-h-0'
                  }`}
                >
                  <div className="p-6 md:p-8 space-y-4 text-sm text-[#3B352F]/80 leading-relaxed">
                    <p>
                      Hvis du er pensionist, studerende eller sygemeldt, kan der i visse tilfælde tilbydes en særlig rabat på individuelle sessioner, så behandlingen bliver mere økonomisk overkommelig.
                    </p>
                    <p>
                      Kontakt mig gerne direkte for at høre nærmere om dine muligheder og priser for din specifikke situation.
                    </p>
                    <div className="pt-2">
                      <Link 
                        to="/kontakt" 
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#193f10] hover:underline"
                      >
                        Kontakt mig for at høre mere <i className="fa-light fa-arrow-right text-[10px]" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* Accordion 5: Faktura til virksomhed ved erhvervscoaching */}
              <div 
                className={`border rounded-xl transition-all duration-300 overflow-hidden ${
                  activeSupport === 4 
                    ? 'border-[#193f10] bg-[#FAF7F2] shadow-sm' 
                    : 'border-[#E8DDCE] bg-white hover:border-[#193f10]/40'
                }`}
              >
                {/* Header */}
                <button 
                  onClick={() => toggleSupport(4)}
                  className="w-full px-6 py-5 flex justify-between items-center text-left gap-4"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                      activeSupport === 4 ? 'bg-[#193f10] text-white' : 'bg-[#FAF7F2] text-[#193f10]'
                    }`}>
                      <i className="fa-light fa-file-invoice-dollar text-lg" />
                    </div>
                    <h3 className="font-serif font-bold text-base md:text-lg text-[#193f10]">
                      Faktura til virksomhed ved erhvervscoaching
                    </h3>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <span className="text-xs font-bold text-[#193f10] bg-[#E8DDCE] px-3 py-1 rounded-full">
                      Erhvervsrettet coaching
                    </span>
                    <i className={`fa-light fa-chevron-down transition-transform duration-300 text-[#193f10] ${
                      activeSupport === 4 ? 'rotate-180' : ''
                    }`} />
                  </div>
                </button>

                {/* Content */}
                <div 
                  className={`transition-all duration-300 ease-in-out ${
                    activeSupport === 4 ? 'max-h-[1000px] border-t border-[#E8DDCE]/60' : 'max-h-0'
                  }`}
                >
                  <div className="p-6 md:p-8 space-y-4 text-sm text-[#3B352F]/80 leading-relaxed">
                    <p>
                      Ved erhvervscoaching kan der udstedes en faktura direkte til din virksomhed eller din arbejdsgiver, hvis forløbet aftales som erhvervsrettet coaching.
                    </p>
                    <p>
                      Du skal selv afklare med din revisor eller bogholder, om udgiften kan fratrækkes som en driftsomkostning for virksomheden.
                    </p>
                    <div className="pt-2">
                      <Link 
                        to="/kontakt" 
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#193f10] hover:underline"
                      >
                        Kontakt mig om erhvervsaftaler <i className="fa-light fa-arrow-right text-[10px]" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* Accordion 6: Et prisvenligt første skridt */}
              <div 
                className={`border rounded-xl transition-all duration-300 overflow-hidden ${
                  activeSupport === 5 
                    ? 'border-[#193f10] bg-[#FAF7F2] shadow-sm' 
                    : 'border-[#E8DDCE] bg-white hover:border-[#193f10]/40'
                }`}
              >
                {/* Header */}
                <button 
                  onClick={() => toggleSupport(5)}
                  className="w-full px-6 py-5 flex justify-between items-center text-left gap-4"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                      activeSupport === 5 ? 'bg-[#193f10] text-white' : 'bg-[#FAF7F2] text-[#193f10]'
                    }`}>
                      <i className="fa-light fa-seedling text-lg" />
                    </div>
                    <h3 className="font-serif font-bold text-base md:text-lg text-[#193f10]">
                      Et prisvenligt første skridt
                    </h3>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <span className="text-xs font-bold text-white bg-[#862e0a] px-3 py-1 rounded-full">
                      Første session 375 kr.
                    </span>
                    <i className={`fa-light fa-chevron-down transition-transform duration-300 text-[#193f10] ${
                      activeSupport === 5 ? 'rotate-180' : ''
                    }`} />
                  </div>
                </button>

                {/* Content */}
                <div 
                  className={`transition-all duration-300 ease-in-out ${
                    activeSupport === 5 ? 'max-h-[1000px] border-t border-[#E8DDCE]/60' : 'max-h-0'
                  }`}
                >
                  <div className="p-6 md:p-8 space-y-4 text-sm text-[#3B352F]/80 leading-relaxed">
                    <p>
                      Ønsker du et mere overskueligt første skridt, kan en første behandlingssession hos Core Hypnose være en enkel måde at få afklaring på, mens du samtidig undersøger dine muligheder for støtte, tilskud eller dækning andre steder. Første behandlingssession koster 375 kr. og varer 50 minutter.
                    </p>
                    <div className="pt-2">
                      <Link 
                        to="/bestil-tid" 
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#193f10] hover:underline"
                      >
                        Book første behandlingssession her <i className="fa-light fa-arrow-right text-[10px]" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>



        {/* BEHOLD BILLED OG RYGESTOP BOKSEN */}
        <section className="py-20">
          <div className="max-w-6xl mx-auto px-6">
            
            {/* Rygestop-forløb Highlighted Box */}
            <div className="bg-[#193f10] text-white rounded-3xl p-8 md:p-16 relative overflow-hidden shadow-xl">
              <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <div>
                  <span className="text-[#B89B5E] font-bold text-xs uppercase tracking-widest block mb-4">Særligt forløb</span>
                  <h2 className="text-3xl md:text-4xl font-serif mb-6">🚭 Rygestop-forløb</h2>
                  <p className="text-white/80 mb-8 leading-relaxed">
                    Skræddersyet til dig, der vil stoppe med at ryge – med effektive teknikker, støtte og opfølgning.
                  </p>
                  <div className="flex items-baseline gap-2 mb-8">
                    <span className="text-5xl font-bold tracking-tight">5.400</span>
                    <span className="text-white/60 font-medium">kr.</span>
                    <span className="ml-4 text-xs text-white/50 uppercase tracking-widest bg-white/10 px-3 py-1 rounded-full">3 x 50 min.</span>
                  </div>
                  <ul className="space-y-4 mb-10">
                    <li className="flex items-center gap-3 text-sm text-white/90">
                      <span className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                        <i className="fa-light fa-check text-[#B89B5E] text-xs" />
                      </span>
                      Effektive teknikker & hypnoterapeutisk støtte
                    </li>
                    <li className="flex items-center gap-3 text-sm text-white/90">
                      <span className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                        <i className="fa-light fa-check text-[#B89B5E] text-xs" />
                      </span>
                      Gyldig i 3 måneder fra opstart
                    </li>
                    <li className="flex items-center gap-3 text-sm text-white/90">
                      <span className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                        <i className="fa-light fa-check text-[#B89B5E] text-xs" />
                      </span>
                      Mulighed for 2 rater á 2.700 kr.
                    </li>
                  </ul>
                  <Link to="/kontakt" className="inline-flex items-center gap-2 bg-[#B89B5E] text-[#193f10] px-8 py-4 rounded-md font-bold hover:bg-[#B89B5E]/90 transition-all shadow-lg hover:shadow-xl">
                    Hør mere om rygestop <i className="fa-light fa-arrow-right" />
                  </Link>
                </div>
                <div className="hidden md:block">
                  <div className="aspect-square bg-white/5 rounded-full flex items-center justify-center border border-white/10 p-12 max-w-[320px] mx-auto">
                    <div className="text-center">
                      <div className="text-6xl mb-4">🚭</div>
                      <p className="text-xs font-medium text-white/40 uppercase tracking-[0.2em]">Bliv røgfri nu</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* FAQ SECTION */}
        <section id="faq" className="py-20 bg-white border-t border-[#E8DDCE]/50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="text-[#862e0a] font-bold text-xs uppercase tracking-widest block mb-3">
                FAQ
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-[#193f10] mb-4">
                Ofte stillede spørgsmål om priser & forløb
              </h2>
              <p className="text-base text-[#3B352F]/80 leading-relaxed max-w-2xl mx-auto">
                Her finder du svar på de mest almindelige spørgsmål om booking, klippekort, betaling og rammerne for forløb hos Core Hypnose.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
              {/* Venstre kolonne */}
              <div className="space-y-6">
                {faqItems
                  .map((item, originalIndex) => ({ item, originalIndex }))
                  .filter(({ originalIndex }) => originalIndex % 2 === 0)
                  .map(({ item, originalIndex }) => (
                    <div 
                      key={originalIndex}
                      className={`border rounded-xl transition-all duration-300 overflow-hidden flex flex-col justify-between ${
                        activeFaq === originalIndex 
                          ? 'border-[#193f10] bg-[#FAF7F2] shadow-sm' 
                          : 'border-[#E8DDCE] bg-white hover:border-[#193f10]/40'
                      }`}
                    >
                      <button 
                        onClick={() => toggleFaq(originalIndex)}
                        className="w-full px-6 py-5 min-h-[5.5rem] md:min-h-[6rem] flex justify-between items-center text-left gap-4 cursor-pointer"
                      >
                        <span className="font-serif font-bold text-base md:text-lg text-[#193f10]">
                          {item.question}
                        </span>
                        <i className={`fa-light fa-chevron-down transition-transform duration-300 text-[#193f10] shrink-0 ${
                          activeFaq === originalIndex ? 'rotate-180' : ''
                        }`} />
                      </button>

                      <div 
                        className={`transition-all duration-300 ease-in-out overflow-hidden ${
                          activeFaq === originalIndex ? 'max-h-[1000px] border-t border-[#E8DDCE]/60' : 'max-h-0'
                        }`}
                      >
                        <div className="p-6 md:p-8 text-sm md:text-base text-[#3B352F]/80 leading-relaxed">
                          {item.answer}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>

              {/* Højre kolonne */}
              <div className="space-y-6">
                {faqItems
                  .map((item, originalIndex) => ({ item, originalIndex }))
                  .filter(({ originalIndex }) => originalIndex % 2 !== 0)
                  .map(({ item, originalIndex }) => (
                    <div 
                      key={originalIndex}
                      className={`border rounded-xl transition-all duration-300 overflow-hidden flex flex-col justify-between ${
                        activeFaq === originalIndex 
                          ? 'border-[#193f10] bg-[#FAF7F2] shadow-sm' 
                          : 'border-[#E8DDCE] bg-white hover:border-[#193f10]/40'
                      }`}
                    >
                      <button 
                        onClick={() => toggleFaq(originalIndex)}
                        className="w-full px-6 py-5 min-h-[5.5rem] md:min-h-[6rem] flex justify-between items-center text-left gap-4 cursor-pointer"
                      >
                        <span className="font-serif font-bold text-base md:text-lg text-[#193f10]">
                          {item.question}
                        </span>
                        <i className={`fa-light fa-chevron-down transition-transform duration-300 text-[#193f10] shrink-0 ${
                          activeFaq === originalIndex ? 'rotate-180' : ''
                        }`} />
                      </button>

                      <div 
                        className={`transition-all duration-300 ease-in-out overflow-hidden ${
                          activeFaq === originalIndex ? 'max-h-[1000px] border-t border-[#E8DDCE]/60' : 'max-h-0'
                        }`}
                      >
                        <div className="p-6 md:p-8 text-sm md:text-base text-[#3B352F]/80 leading-relaxed">
                          {item.answer}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </section>

        {/* START MED EN FØRSTE BEHANDLINGSSESSION */}
        <section className="py-16 bg-[#FAF7F2]">
          <div className="max-w-6xl mx-auto px-6">
            <div className="relative bg-[#193f10] rounded-3xl p-8 md:p-14 shadow-xl overflow-hidden">
              {/* Background pattern */}
              <div className="absolute inset-0 opacity-5 bg-[radial-gradient(#FAF7F2_1px,transparent_1px)] [background-size:16px_16px]" />
              <div className="absolute -right-20 -bottom-20 w-80 h-80 rounded-full bg-white/5 pointer-events-none" />
              
              <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                {/* Left: Text */}
                <div className="lg:col-span-7 text-left">
                  <span className="text-[#b59b5a] font-bold text-xs uppercase tracking-widest block mb-3">
                    Er du i tvivl? · Tag det første skridt i dit eget tempo
                  </span>
                  <h2 className="text-3xl md:text-4xl font-serif text-white mb-4">
                    Start med første behandlingssession
                  </h2>
                  <p className="text-sm md:text-base text-[#FAF7F2]/90 leading-relaxed mb-2">
                    Du behøver ikke vælge et længere forløb på forhånd. Start med 375 kr.-sessionen, få en grundig afklaring og din behandlingsplan, og tag stilling til næste skridt bagefter.
                  </p>
                  <p className="text-xs text-[#FAF7F2]/75">
                    Sessionen varer 50 minutter og kan foregå i klinikken i Valby eller online via video.
                  </p>
                </div>

                {/* Right: Buttons */}
                <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col gap-4 justify-start lg:justify-center items-stretch sm:items-center lg:items-stretch w-full">
                  <Link 
                    to="/bestil-tid" 
                    className="inline-flex items-center justify-center gap-2 bg-[#B89B5E] text-[#123F13] px-8 py-4 rounded-md font-bold hover:bg-[#a3884b] transition-all shadow-lg hover:shadow-xl text-center"
                  >
                    Book første behandlingssession <i className="fa-light fa-arrow-right" />
                  </Link>
                  <div className="flex gap-3">
                    <a 
                      href="#faq" 
                      className="flex-1 px-5 py-3.5 bg-white/10 text-white border border-white/20 font-semibold rounded-md hover:bg-white/20 transition-all duration-200 flex items-center justify-center gap-2 text-center text-xs"
                    >
                      <i className="fa-light fa-circle-question" /> Læs FAQ
                    </a>
                    <Link 
                      to="/kontakt" 
                      className="flex-1 px-5 py-3.5 bg-white/10 text-white border border-white/20 font-semibold rounded-md hover:bg-white/20 transition-all duration-200 flex items-center justify-center gap-2 text-center text-xs"
                    >
                      <i className="fa-light fa-envelope" /> Kontakt mig
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* HAR DU SPØRGSMÅL? */}
        <section className="py-20 bg-white border-t border-[#E8DDCE]/50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
              
              {/* Left Column: Text & CTAs */}
              <div className="lg:col-span-6 text-center lg:text-left">
                <h2 className="text-3xl md:text-4xl font-serif text-[#193f10] mb-4">
                  Har du spørgsmål?
                </h2>
                <p className="text-base text-[#3B352F]/80 leading-relaxed mb-8 max-w-2xl mx-auto lg:mx-0">
                  Du behøver ikke vide præcis, hvad du har brug for på forhånd. Du er velkommen til at kontakte mig, hvis du er i tvivl om, hvilken session eller hvilket forløb der passer bedst til dig.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start items-center">
                  <Link 
                    to="/bestil-tid" 
                    className="w-full sm:w-auto px-8 py-4 bg-[#193f10] text-white font-bold rounded-md hover:bg-[#193f10]/90 transition-all duration-200 shadow-lg shadow-[#193f10]/10 flex items-center justify-center gap-2"
                  >
                    <i className="fa-light fa-calendar-check" /> Book tid
                  </Link>
                  <Link 
                    to="/kontakt" 
                    className="w-full sm:w-auto bg-white border border-[#E8DDCE] text-[#3B352F] px-8 py-4 rounded-md font-semibold hover:bg-[#FAF7F2] transition-all flex items-center justify-center gap-2"
                  >
                    <i className="fa-light fa-envelope" /> Send besked
                  </Link>
                </div>
              </div>

              {/* Right Column: Image */}
              <div className="lg:col-span-6">
                <div className="rounded-2xl overflow-hidden shadow-lg border border-[#E8DDCE]/60">
                  <img 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/8ff806db-b665-42b0-97e5-34422b7bc8b8.png" 
                    alt="Priser og booking" 
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>

            </div>
          </div>
        </section>

      </div>
    </>
  );
};

export default Priser;
